import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import { formatCredentials } from "../utils/formatCredentials";
import axios from "axios";
import { getConfigWithToken } from "../config/config";
import formatDate from "../utils/formatDate";
import { formatDateOnly, formatDateForBackend } from "../utils/formatDateOnly";
import styles from "../outlets/SessionHistoryDetailView.module.css";
import SessionActionButtons from "../components/SessionActionButtons";
import SignAndLockCard from "../components/SignAndLockCard";
import AddendumView from "./AddendumView";
import AppointmentDetailsSection from "./sessionHistorySections/AppointmentDetailsSection";
import CarePlanConcernsSection from "./sessionHistorySections/CarePlanConcernsSection";
import ClientPresentationSection from "./sessionHistorySections/ClientPresentationSection";
import InterventionsSection from "./sessionHistorySections/InterventionsSection";
import ClientProgressSection from "./sessionHistorySections/ClientProgressSection";
import FollowUpPDFGenerator from "./FollowUpPDFGenerator";

const FollowUp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, openAddendum, editExistingAddendum } = location.state || {};
  const backPath = location.state?.from || "/session-history";

  const [saving, setSaving] = useState(false);
  const [signingAndSubmitting, setSigningAndSubmitting] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSavedTimestamp, setLastSavedTimestamp] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState("Save");
  const [retryTimeoutId, setRetryTimeoutId] = useState(null);
  const [lastSavedData, setLastSavedData] = useState(null);
  const [activeSection, setActiveSection] = useState("appointment-details");
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isSigned, setIsSigned] = useState(false);
  const [isSignedFromDB, setIsSignedFromDB] = useState(false); // Track isSigned from database
  const [carePlanEditMode, setCarePlanEditMode] = useState(null); // null = view, "update" = update mode, "create" = create new mode
  const [originalCarePlanData, setOriginalCarePlanData] = useState(null); // Store original data for update mode
  const [carePlanSaving, setCarePlanSaving] = useState(false); // Separate saving state for care plan
  const [addAddendum, setAddAddendum] = useState(false);
  const [addendumData, setAddendumData] = useState([]);
  const [currentAddendumIndex, setCurrentAddendumIndex] = useState(null); // Track which addendum we're editing
  const addendumInitializedRef = useRef(false); // Track if addendum textarea has been initialized
  const errorToastShownRef = useRef(false);
  const [expandedSections, setExpandedSections] = useState([]); // For expandable sections in read-only view
  const [userDiagnosisAndCarePlans, setUserDiagnosisAndCarePlans] = useState({
    diagnosis: { active: [], inactive: [], all: [] },
    carePlans: { active: null, inactive: [], all: [] }
  });
  const [loadingDiagnosisAndCarePlans, setLoadingDiagnosisAndCarePlans] = useState(false);

  const user = useSelector((state) => state?.user);

  // Form data state
  const [sessionFormData, setSessionFormData] = useState({
    clientGoal: "",
    objectives: "",
    interventions: "",
    sessionFrequency: "",
    otherSessionFrequencyText: "",
    reviewDate: "",
    timeIn: "",
    timeOut: "",
    wasClientAtHome: "",
    // Diagnosis fields
    primaryDiagnosis: "",
    primaryDiagnosisDescription: "",
    secondaryDiagnosis: "",
    secondaryDiagnosisDescription: "",
    tertiaryDiagnosis: "",
    tertiaryDiagnosisDescription: "",
    // Client Presentation fields
    focusOfSession: "",
    selectedSymptoms: [],
    otherSymptomText: "",
    moodAffect: "",
    otherMoodAffectText: "",
    behaviorEngagement: "",
    otherBehaviorEngagementText: "",
    // Interventions fields
    interventionsUsed: "",
    // Client Progress fields
    clientResponseToCare: "",
    progressTowardGoals: [],
    goalMetDescription: "",
    barriersToProgress: [],
    otherBarrierText: "",
    additionalNotes: "",
    signedAt: null,
    isSigned: false,
    addendumText: "",
  });

  // Error states for Care Plan
  const [clientGoalError, setClientGoalError] = useState("");
  const [objectivesError, setObjectivesError] = useState("");
  const [interventionsError, setInterventionsError] = useState("");

  // Error states for Client Progress
  const [clientResponseToCareError, setClientResponseToCareError] = useState("");
  const [progressTowardGoalsError, setProgressTowardGoalsError] = useState("");
  const [barriersToProgressError, setBarriersToProgressError] = useState("");

  const [sessionFrequencyError, setSessionFrequencyError] = useState("");
  const [reviewDateError, setReviewDateError] = useState("");

  // Error states for Client Presentation
  const [focusOfSessionError, setFocusOfSessionError] = useState("");
  const [symptomsError, setSymptomsError] = useState("");
  const [moodAffectError, setMoodAffectError] = useState("");
  const [behaviorEngagementError, setBehaviorEngagementError] = useState("");

  // Error states for Interventions
  const [interventionsUsedError, setInterventionsUsedError] = useState("");

  // Error states for Appointment Details
  const [timeInError, setTimeInError] = useState("");
  const [timeOutError, setTimeOutError] = useState("");
  const [wasClientAtHomeError, setWasClientAtHomeError] = useState("");

  // Error states for Client Progress additional fields
  const [goalMetDescriptionError, setGoalMetDescriptionError] = useState("");
  const [additionalNotesError, setAdditionalNotesError] = useState("");

  // Error state for Addendum
  const [addendumTextError, setAddendumTextError] = useState("");

  // Diagnosis states
  const [primaryDiagnosisError, setPrimaryDiagnosisError] = useState("");
  const [primaryDiagnosisSearchTerm, setPrimaryDiagnosisSearchTerm] = useState("");
  const [primaryDiagnosisResults, setPrimaryDiagnosisResults] = useState([]);
  const [diagnosisSearchLoading, setDiagnosisSearchLoading] = useState(false);
  const [diagnosisSearchError, setDiagnosisSearchError] = useState("");
  const [isPrimaryDiagnosisDropdownOpen, setIsPrimaryDiagnosisDropdownOpen] = useState(false);
  const diagnosisCodeRef = useRef(null);

  const [secondaryDiagnosisSearchTerm, setSecondaryDiagnosisSearchTerm] = useState("");
  const [secondaryDiagnosisResults, setSecondaryDiagnosisResults] = useState([]);
  const [secondaryDiagnosisSearchLoading, setSecondaryDiagnosisSearchLoading] = useState(false);
  const [secondaryDiagnosisSearchError, setSecondaryDiagnosisSearchError] = useState("");
  const [isSecondaryDiagnosisDropdownOpen, setIsSecondaryDiagnosisDropdownOpen] = useState(false);
  const secondaryDiagnosisCodeRef = useRef(null);

  const [tertiaryDiagnosisSearchTerm, setTertiaryDiagnosisSearchTerm] = useState("");
  const [tertiaryDiagnosisResults, setTertiaryDiagnosisResults] = useState([]);
  const [tertiaryDiagnosisSearchLoading, setTertiaryDiagnosisSearchLoading] = useState(false);
  const [tertiaryDiagnosisSearchError, setTertiaryDiagnosisSearchError] = useState("");
  const [isTertiaryDiagnosisDropdownOpen, setIsTertiaryDiagnosisDropdownOpen] = useState(false);
  const tertiaryDiagnosisCodeRef = useRef(null);

  // Time picker states
  const [isTimeInPickerOpen, setIsTimeInPickerOpen] = useState(false);
  const [timeInParts, setTimeInParts] = useState({ hour: "", minute: "", period: "" });
  const timeInPickerRef = useRef(null);
  const timeInUpdateFromInputRef = useRef(false);

  const [isTimeOutPickerOpen, setIsTimeOutPickerOpen] = useState(false);
  const [timeOutParts, setTimeOutParts] = useState({ hour: "", minute: "", period: "" });
  const timeOutPickerRef = useRef(null);
  const timeOutUpdateFromInputRef = useRef(false);

  const handleBackNavigation = useCallback(() => {
    if (backPath) {
      navigate(backPath, { replace: true });
      return;
    }

    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/session-history");
    }
  }, [navigate, backPath]);

  // Helper function to process addendumData - ensures addendumSignedAt is present in every entry
  // By default null, and only updates to timestamp when addendumSigned becomes true
  // Once addendumSigned is true, the entry is locked and cannot be modified
  const processAddendumData = useCallback((data) => {
    if (!data || !Array.isArray(data)) {
      return data || [];
    }
    return data.map((entry) => {
      // If addendumSigned is true, lock the entry - no modification
      // Preserve entry exactly as-is (signed entries are immutable)
      if (entry.addendumSigned === true) {
        return entry; // Locked entry - no changes allowed
      }

      // If addendumSigned is false, ensure addendumSignedAt is null
      return {
        ...entry,
        addendumSignedAt: null
      };
    });
  }, []);

  const handleAddAddendum = () => {
    setAddAddendum(true);
  };

  // Check if latest addendum entry can be edited (addendumSigned: false, addendumSubmit: true)
  const canEditAddendum = () => {
    if (!addendumData || addendumData.length === 0) {
      return false;
    }
    const latestIndex = addendumData.length - 1;
    const latestAddendum = addendumData[latestIndex];
    return latestAddendum &&
      latestAddendum.addendumSigned === false &&
      latestAddendum.addendumSubmit === true;
  };

  // Handle Edit Addendum button click
  const handleEditAddendum = () => {
    if (!addendumData || addendumData.length === 0) {
      return;
    }
    const latestIndex = addendumData.length - 1;
    const latestAddendum = addendumData[latestIndex];

    // Set addendumSubmit to false in the latest object
    if (latestAddendum) {
      const updatedAddendumData = [...addendumData];
      updatedAddendumData[latestIndex] = {
        ...updatedAddendumData[latestIndex],
        addendumSubmit: false
      };
      setAddendumData(updatedAddendumData);

      if (latestAddendum.addendumText) {
        setSessionFormData(prev => ({ ...prev, addendumText: latestAddendum.addendumText || "" }));
        setCurrentAddendumIndex(latestIndex);
        setAddAddendum(true);
        addendumInitializedRef.current = false; // Reset initialization to load the text
      }
    }
  };

  // New function for addendum submit - checks if latest addendumText is not empty
  const handleAddendumSubmit = async (e) => {
    e?.stopPropagation?.();

    if (!session?._id) {
      toast.error("Session ID is missing");
      return;
    }

    // Check: Validate CURRENT addendumText from sessionFormData (the latest value user typed)
    if (!sessionFormData.addendumText || sessionFormData.addendumText.trim() === "") {
      setAddendumTextError("Please add addendum text before submitting");
      toast.error("Please add addendum text before submitting");
      return;
    }

    setAddendumTextError("");

    try {
      setSigningAndSubmitting(true);

      // CRITICAL: Update addendumData with the CURRENT addendumText from sessionFormData
      // This ensures we save the latest text, not the old auto-saved value
      const updatedAddendumData = [...addendumData];
      const currentAddendumText = sessionFormData.addendumText.trim();

      // If addendumData is empty, create new entry with current text
      if (!updatedAddendumData || updatedAddendumData.length === 0) {
        updatedAddendumData.push({
          addendumText: currentAddendumText,
          addendumSigned: false,
          addendumSubmit: true, // Mark as submitted
          addendumSignedAt: null,
          createdAt: new Date().toISOString()
        });
      } else {
        // Get latest entry
        const latestIndex = updatedAddendumData.length - 1;
        const latestAddendum = updatedAddendumData[latestIndex];

        // If latest entry is signed, create new entry
        if (latestAddendum && latestAddendum.addendumSigned === true) {
          updatedAddendumData.push({
            addendumText: currentAddendumText,
            addendumSigned: false,
            addendumSubmit: true,
            addendumSignedAt: null,
            createdAt: new Date().toISOString()
          });
        }
        // If latest entry is not signed, update it with CURRENT text and set addendumSubmit: true
        else if (latestAddendum && latestAddendum.addendumSigned === false) {
          updatedAddendumData[latestIndex] = {
            ...latestAddendum,
            addendumText: currentAddendumText, // Use CURRENT text, not old state
            addendumSubmit: true, // Mark as submitted
            addendumSigned: false,
            addendumSignedAt: null,
            updatedAt: new Date().toISOString()
          };
        }
        // Fallback: create new entry
        else {
          updatedAddendumData.push({
            addendumText: currentAddendumText,
            addendumSigned: false,
            addendumSubmit: true,
            addendumSignedAt: null,
            createdAt: new Date().toISOString()
          });
        }
      }

      // Process addendumData to ensure proper structure
      const processedAddendumData = processAddendumData(updatedAddendumData);

      // Build full payload
      const payload = buildSavePayload(false);

      // Override addendumData with the updated one (contains latest text)
      payload.addendumData = processedAddendumData;

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/createSessionHistory`,
        payload,
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success) {
        // Use response data if available (backend may have modified it), otherwise use payload
        const responseAddendumData = response.data.data?.addendumData;
        const addendumDataToUse = (responseAddendumData && Array.isArray(responseAddendumData) && responseAddendumData.length > 0)
          ? responseAddendumData
          : (payload.addendumData && Array.isArray(payload.addendumData) && payload.addendumData.length > 0)
            ? payload.addendumData
            : null;

        // Update addendumData state immediately with the submitted data
        if (addendumDataToUse) {
          const updatedAddendumData = processAddendumData([...addendumDataToUse]);

          // Update addendumText from latest addendum BEFORE updating state
          const latestIndex = updatedAddendumData.length - 1;
          const latestAddendum = updatedAddendumData[latestIndex];

          // Check if addendum can still be edited (addendumSubmit: true, addendumSigned: false)
          const canStillEdit = latestAddendum &&
            latestAddendum.addendumSigned === false &&
            latestAddendum.addendumSubmit === true;

          // Update state - React will batch these updates
          setAddendumData(updatedAddendumData);

          if (latestAddendum && latestAddendum.addendumText) {
            setSessionFormData(prev => ({
              ...prev,
              addendumText: latestAddendum.addendumText || ""
            }));
            setCurrentAddendumIndex(latestIndex);
          }

          // Set addAddendum based on whether addendum can still be edited
          // After submit: addendumSubmit=true, addendumSigned=false → canEditAddendum() will return true
          // We set addAddendum to false so canEditAddendum() controls the display
          // This shows the "edit addendum" view (read-only) where user can sign and lock
          if (canStillEdit) {
            // Addendum is submitted but not signed - set addAddendum to false
            // canEditAddendum() will return true after state update, so section will show
            // This ensures the "edit addendum" view (read-only) shows where user can sign and lock
            setAddAddendum(false);
            // Reset initialization flag so text loads correctly if user clicks "Edit Addendum" later
            addendumInitializedRef.current = false;
          } else {
            // Addendum is signed or doesn't exist - hide addendum section
            setAddAddendum(false);
          }
        } else {
          // No addendum data - hide section
          setAddAddendum(false);
        }

        setIsSubmit(true);

        clearAllErrors(); // Clear all errors on successful submit
        toast.success("Addendum submitted successfully!", {
          position: "bottom-right",
          autoClose: 3000,
        });
      } else {
        // Show all validation errors from backend
        if (response.data.errors && Array.isArray(response.data.errors)) {
          response.data.errors.forEach((error, index) => {
            setTimeout(() => {
              toast.error(error);
            }, index * 300);
          });
        } else {
          toast.error(response.data.message || "Failed to submit addendum");
        }
      }
    } catch (error) {
      console.error("Error submitting addendum:", error);
      // Show all validation errors from backend
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        error.response.data.errors.forEach((err, index) => {
          setTimeout(() => {
            toast.error(err);
          }, index * 300);
        });
      } else {
        toast.error(
          error.response?.data?.message ||
          "An error occurred while submitting addendum"
        );
      }
    } finally {
      setSigningAndSubmitting(false);
    }
  };

  const handleSave = async (e) => {
    e.stopPropagation();

    if (!session?._id) {
      toast.error("Session ID is missing");
      return;
    }

    try {
      setSaving(true);
      setSaveStatus("Saving...");
      const payload = buildSavePayload(true); // Manual save - no validation, save whatever data is present
      payload.isSaveDiagnosis = true;
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/createSessionHistory`,
        payload,
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success) {
        const currentData = JSON.stringify(payload);
        setLastSavedTimestamp(new Date());
        setHasUnsavedChanges(false);
        setLastSavedData(currentData);
        setSaveStatus("Saved");

        // Update addendumData state if addAddendum is true
        if (addAddendum && payload.addendumData) {
          setAddendumData(processAddendumData(payload.addendumData));
        }

        clearAllErrors(); // Clear all errors on successful save
        errorToastShownRef.current = false; // Reset error toast state on successful save
        toast.dismiss("auto-save-error"); // Dismiss error toast if it exists

        toast.success("💾 Follow-up saved successfully.", {
          position: "bottom-right",
          autoClose: 3000,
        });

        // Revert button text after 2 seconds
        setTimeout(() => {
          setSaveStatus("Save");
        }, 2000);
      } else {
        // Show all validation errors from backend
        if (response.data.errors && Array.isArray(response.data.errors)) {
          response.data.errors.forEach((error, index) => {
            setTimeout(() => {
              toast.error(error);
            }, index * 300);
          });
        } else {
          toast.error(response.data.message || "Failed to save follow-up");
        }
        setSaveStatus("Save");
      }
    } catch (error) {
      console.error("Error saving follow-up:", error);
      setSaveStatus("Save");

      // Show error banner
      toast.error("Unable to save progress. Please check your connection.", {
        position: "bottom-right",
        autoClose: false,
      });

      // Show all validation errors from backend
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        error.response.data.errors.forEach((err, index) => {
          setTimeout(() => {
            toast.error(err);
          }, index * 300);
        });
      } else {
        toast.error(
          error.response?.data?.message ||
          "An error occurred while saving follow-up"
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCarePlanSave = async (e) => {
    e?.stopPropagation();

    if (!session?._id) {
      toast.error("Session ID is missing");
      return;
    }

    // Validate all required care plan fields before saving
    const errors = [];
    const errorSetters = {};

    // Client Goal validation
    if (!sessionFormData.clientGoal || sessionFormData.clientGoal.trim() === "") {
      errors.push("Client Goal is required");
      errorSetters.clientGoalError = "Client Goal is required";
    }

    // Objectives validation
    if (!sessionFormData.objectives || sessionFormData.objectives.trim() === "") {
      errors.push("Objectives is required");
      errorSetters.objectivesError = "Objectives is required";
    }

    // Interventions validation
    if (!sessionFormData.interventions || sessionFormData.interventions.trim() === "") {
      errors.push("Interventions is required");
      errorSetters.interventionsError = "Interventions is required";
    }

    // Session Frequency validation
    if (!sessionFormData.sessionFrequency || sessionFormData.sessionFrequency === "") {
      errors.push("Session Frequency is required");
      errorSetters.sessionFrequencyError = "Session Frequency is required";
    }

    // If frequency is "Other", validate otherSessionFrequencyText
    if (sessionFormData.sessionFrequency === "Other" && (!sessionFormData.otherSessionFrequencyText || sessionFormData.otherSessionFrequencyText.trim() === "")) {
      errors.push("Please specify frequency");
      errorSetters.sessionFrequencyError = "Please specify frequency";
    }

    // Review Date validation
    if (!sessionFormData.reviewDate || sessionFormData.reviewDate === "") {
      errors.push("Review Date is required");
      errorSetters.reviewDateError = "Review Date is required";
    }

    // Set all error states
    if (errors.length > 0) {
      if (errorSetters.clientGoalError) setClientGoalError(errorSetters.clientGoalError);
      if (errorSetters.objectivesError) setObjectivesError(errorSetters.objectivesError);
      if (errorSetters.interventionsError) setInterventionsError(errorSetters.interventionsError);
      if (errorSetters.sessionFrequencyError) setSessionFrequencyError(errorSetters.sessionFrequencyError);
      if (errorSetters.reviewDateError) setReviewDateError(errorSetters.reviewDateError);

      // Show error toasts
      errors.forEach((error, index) => {
        setTimeout(() => {
          toast.error(error);
        }, index * 300);
      });

      // Scroll to first error field
      const firstErrorField = document.querySelector('[id="care-plan"]');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      return; // Prevent save if validation fails
    }

    try {
      setCarePlanSaving(true);

      // Get userId from session
      const userId = session?.user_fk || session?.user?._id;
      if (!userId) {
        toast.error("User ID is missing");
        return;
      }

      // Get client timezone (same as AddAppointments.js and CarePlanCard.jsx)
      const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Format review date to ensure it's in YYYY-MM-DD format before sending
      // HTML date inputs always provide YYYY-MM-DD format, so we use it directly
      // IMPORTANT: Never convert to Date object - keep as string to avoid timezone shifts
      let formattedReviewDate = sessionFormData.reviewDate || "";

      // Ensure it's in YYYY-MM-DD format (date inputs should already be in this format)
      // Only normalize if it's not already in the correct format
      if (formattedReviewDate && !/^\d{4}-\d{2}-\d{2}$/.test(formattedReviewDate)) {
        // If not in YYYY-MM-DD format, try to extract it from ISO string or other formats
        const isoMatch = formattedReviewDate.match(/^(\d{4}-\d{2}-\d{2})/);
        if (isoMatch) {
          formattedReviewDate = isoMatch[1];
        }
      }

      // Build payload similar to CarePlanCard.jsx
      let payload;

      if (carePlanEditMode === "update" && userDiagnosisAndCarePlans?.carePlans?.active) {
        // For update: send the complete active care plan with updated form values
        const activeCarePlan = userDiagnosisAndCarePlans.carePlans.active;
        payload = {
          ...activeCarePlan,
          clientGoal: sessionFormData.clientGoal || "",
          objectives: sessionFormData.objectives || "",
          interventions: sessionFormData.interventions || "",
          frequency: sessionFormData.sessionFrequency || "",
          otherSessionFrequencyText: sessionFormData.otherSessionFrequencyText || "",
          reviewDate: formattedReviewDate,
          action: "update",
          // Ensure version number is included
          version: activeCarePlan.version || activeCarePlan.versionNumber,
          timeZone: clientTimeZone,
        };
      } else {
        // For create: send only form fields
        payload = {
          clientGoal: sessionFormData.clientGoal || "",
          objectives: sessionFormData.objectives || "",
          interventions: sessionFormData.interventions || "",
          frequency: sessionFormData.sessionFrequency || "",
          otherSessionFrequencyText: sessionFormData.otherSessionFrequencyText || "",
          reviewDate: formattedReviewDate,
          action: "create",
          timeZone: clientTimeZone,
        };
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/users/create-or-update-care-plan/${userId}`,
        payload,
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success) {
        // Update original care plan data with current values after successful save
        const currentCarePlanData = {
          clientGoal: sessionFormData.clientGoal || "",
          objectives: sessionFormData.objectives || "",
          interventions: sessionFormData.interventions || "",
          sessionFrequency: sessionFormData.sessionFrequency || "",
          otherSessionFrequencyText: sessionFormData.otherSessionFrequencyText || "",
          reviewDate: sessionFormData.reviewDate || "",
        };
        setOriginalCarePlanData(currentCarePlanData);

        // Reset unsaved changes since we just saved
        setHasUnsavedChanges(false);
        setLastSavedTimestamp(new Date());

        // Exit edit mode after successful save
        setCarePlanEditMode(null);

        clearAllErrors(); // Clear all errors on successful save

        // Refresh diagnosis and care plans data to get updated care plan
        await fetchUserDiagnosisAndCarePlans();

        toast.success("💾 Care plan saved successfully.", {
          position: "bottom-right",
          autoClose: 3000,
        });
      } else {
        // Show all validation errors from backend
        if (response.data.errors && Array.isArray(response.data.errors)) {
          response.data.errors.forEach((error, index) => {
            setTimeout(() => {
              toast.error(error);
            }, index * 300);
          });
        } else {
          toast.error(response.data.message || "Failed to save care plan");
        }
      }
    } catch (error) {
      console.error("Error saving care plan:", error);

      // Show all validation errors from backend
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        error.response.data.errors.forEach((err, index) => {
          setTimeout(() => {
            toast.error(err);
          }, index * 300);
        });
      } else {
        toast.error(
          error.response?.data?.message ||
          "Unable to save care plan. Please try again."
        );
      }
    } finally {
      setCarePlanSaving(false);
    }
  };

  const handleEdit = async (e) => {
    e.stopPropagation();

    if (!session?._id) {
      toast.error("Session ID is missing");
      return;
    }

    try {
      setSaving(true);
      const payload = buildSavePayload(true);
      payload.isSubmit = false; // Explicitly set isSubmit to false for Edit

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/createSessionHistory`,
        payload,
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success) {
        setIsSubmit(false);
        toast.info("You can now edit the follow-up");
      } else {
        toast.error(response.data.message || "Failed to update follow-up");
      }
    } catch (error) {
      console.error("Error updating follow-up:", error);
      toast.error("Unable to update. Please check your connection.");
    } finally {
      setSaving(false);
    }
  };

  const handleSignAndLock = async () => {
    if (!session?._id) {
      toast.error("Session ID is missing");
      return;
    }

    try {
      setSigningAndSubmitting(true);

      // Optimized: If signing addendum, only send minimal data
      if (canEditAddendum() && addendumData && Array.isArray(addendumData) && addendumData.length > 0) {
        const latestIndex = addendumData.length - 1;
        const latestAddendum = addendumData[latestIndex];

        if (latestAddendum && latestAddendum.addendumSigned === false) {
          try {
            // Create optimized payload with only sessionId and updated addendumData
            const updatedAddendumData = [...addendumData];
            updatedAddendumData[latestIndex] = {
              ...latestAddendum,
              addendumSigned: true,
              // Backend will set addendumSignedAt automatically, but we can set it here for immediate UI update
              addendumSignedAt: new Date().toISOString()
            };

            // Send minimal payload - only sessionId and addendumData
            // isAutoSave: true bypasses validation checks
            const response = await axios.post(
              `${process.env.REACT_APP_BASE_URL}/sessionHistory/createSessionHistory`,
              {
                sessionId: session._id,
                addendumData: updatedAddendumData,
                isSigned: true,
                isFollowUp: true,
                isAutoSave: true,
              },
              getConfigWithToken(user.user?.token)
            );

            if (response.data.success) {
              toast.success("Note signed and locked successfully.", {
                position: "bottom-right",
                autoClose: 3000,
              });

              // Get signedAt timestamp from response
              const signedAtTimestamp = response.data.data?.signedAt || new Date().toISOString();

              setSessionFormData(prev => ({
                ...prev,
                isSigned: true,
                signedAt: signedAtTimestamp
              }));
              setIsSigned(true);
              setIsSignedFromDB(true);
              setIsSubmit(true);

              // Update addendumData state with signed entry
              setAddendumData(updatedAddendumData);
              return; // Early return - don't execute the full payload code below
            } else {
              toast.error(response.data.message || "Failed to sign and lock note.");
              return;
            }
          } catch (error) {
            console.error("Error signing addendum:", error);
            toast.error(
              error.response?.data?.message ||
              "An error occurred while signing addendum."
            );
            return;
          }
        }
      }
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/createSessionHistory`,
        {
          sessionId: session._id,
          isSigned: true,
          isFollowUp: true,
          isAutoSave: true,
        },
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success) {
        toast.success("Note signed and locked successfully.", {
          position: "bottom-right",
          autoClose: 3000,
        });

        // Get signedAt timestamp from response
        const signedAtTimestamp = response.data.data?.signedAt || new Date().toISOString();

        setSessionFormData(prev => ({
          ...prev,
          isSigned: true,
          signedAt: signedAtTimestamp
        }));
        setIsSigned(true);
        setIsSignedFromDB(true);
        setIsSubmit(true);

        try {
          await axios.put(
            `${process.env.REACT_APP_BASE_URL}/appointment/update-status/${session._id}`,
            { status: "Completed" },
            getConfigWithToken(user.user?.token)
          );
        } catch (statusError) {
          console.error("❌ Error updating appointment status:", statusError);
        }
      } else {
        toast.error(response.data.message || "Failed to sign and lock note.");
      }
    } catch (error) {
      console.error("Error signing and locking note:", error);
      toast.error(
        error.response?.data?.message ||
        "An error occurred while signing and locking note."
      );
    } finally {
      setSigningAndSubmitting(false);
    }
  };

  const handleDownload = async () => {
    if (!session?._id) {
      toast.error("Session ID is missing");
      return;
    }

    try {
      // Prepare data for PDF generation
      const clientName = `${session.user?.firstname || ""} ${session.user?.lastname || ""}`.trim();
      const providerName = `${session.health_specialist?.firstname || ""} ${session.health_specialist?.lastname || ""}`.trim();
      const providerCredentials = formatCredentials(session?.health_specialist?.credentials) || "LPC";
      const appointmentDate = session.appointmentdate || session.appointmenttime;

      // Get client DOB
      const clientDOB = session.user?.dob
        ? formatDate(session.user.dob)
        : "N/A";

      // Get signed date/time from database
      const getSignedDateFromDB = () => {
        if (!sessionFormData?.signedAt) return null;
        return new Date(sessionFormData.signedAt).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric"
        });
      };

      const getSignedTimeFromDB = () => {
        if (!sessionFormData?.signedAt) return null;
        return new Date(sessionFormData.signedAt).toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true
        });
      };

      // Format time function
      const formatDateTime = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      };

      const appointmentTime = session.appointmenttime ? formatDateTime(session.appointmenttime) : "N/A";

      // Fetch addendumData from database
      let addendumDataArray = [];
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/sessionHistory/get-by-session/${session._id}`,
          getConfigWithToken(user?.user?.token)
        );
        if (response.data.success && response.data.data?.addendumData) {
          addendumDataArray = Array.isArray(response.data.data.addendumData)
            ? response.data.data.addendumData
            : [];
        }
      } catch (error) {
        console.error("Error fetching addendum data:", error);
        addendumDataArray = [];
      }

      // Get generatePDF function from the PDF generator component
      const generatePDF = FollowUpPDFGenerator({
        sessionFormData,
        clientName,
        providerName,
        providerCredentials: formatCredentials(session?.health_specialist?.credentials) || "LPC",
        appointmentDate,
        appointmentTime,
        clientDOB,
        isSigned: isSigned || sessionFormData.isSigned,
        getSignedDateFromDB,
        getSignedTimeFromDB,
        formatDate,
        derivedDurationMinutes,
        addendumDataArray,
      });

      generatePDF();
    } catch (error) {
      console.error("Error downloading follow-up:", error);
      toast.error("An error occurred while downloading the follow-up.");
    }
  };

  const handleSignAndSubmit = async (e) => {
    e?.stopPropagation();

    if (!session?._id) {
      toast.error("Session ID is missing");
      return;
    }

    // Collect all validation errors
    const errors = [];
    const errorSetters = {};

    // Appointment Details - Time In and Time Out are required
    if (!sessionFormData.timeIn || sessionFormData.timeIn.trim() === "") {
      errors.push("Time In is required");
      errorSetters.timeInError = "Time In is required";
    }

    if (!sessionFormData.timeOut || sessionFormData.timeOut.trim() === "") {
      errors.push("Time Out is required");
      errorSetters.timeOutError = "Time Out is required";
    }

    if (!sessionFormData.wasClientAtHome || sessionFormData.wasClientAtHome === "" || sessionFormData.wasClientAtHome === "-") {
      errors.push("Was the client at home? is required");
      errorSetters.wasClientAtHomeError = "Was the client at home? is required";
    }

    // Diagnosis - Primary Diagnosis is REQUIRED
    if (!sessionFormData.primaryDiagnosis || sessionFormData.primaryDiagnosis.trim() === "" || sessionFormData.primaryDiagnosis === "-") {
      errors.push("Primary Diagnosis is required");
      errorSetters.primaryDiagnosisError = "Primary Diagnosis is required. Must select one ICD-10 diagnosis.";
    }

    // Client Presentation - All fields are required
    if (!sessionFormData.focusOfSession || sessionFormData.focusOfSession.trim() === "") {
      errors.push("Focus of Session is required");
      errorSetters.focusOfSessionError = "Focus of Session is required";
    }

    if (!sessionFormData.selectedSymptoms || sessionFormData.selectedSymptoms.length === 0) {
      errors.push("At least one symptom must be selected");
      errorSetters.symptomsError = "At least one symptom must be selected";
    }

    if (!sessionFormData.moodAffect || sessionFormData.moodAffect === "") {
      errors.push("Mood/Affect is required");
      errorSetters.moodAffectError = "Mood/Affect is required";
    }

    if (!sessionFormData.behaviorEngagement || sessionFormData.behaviorEngagement === "") {
      errors.push("Behavior/Engagement is required");
      errorSetters.behaviorEngagementError = "Behavior/Engagement is required";
    }

    // Interventions - Required
    if (!sessionFormData.interventionsUsed || sessionFormData.interventionsUsed.trim() === "") {
      errors.push("Interventions Used is required");
      errorSetters.interventionsUsedError = "Interventions Used is required";
    }

    // Client Progress - All fields are required
    if (!sessionFormData.clientResponseToCare || sessionFormData.clientResponseToCare.trim() === "") {
      errors.push("Client Response to Care is required");
      errorSetters.clientResponseToCareError = "Client Response to Care is required";
    }

    if (!sessionFormData.progressTowardGoals || sessionFormData.progressTowardGoals.length === 0) {
      errors.push("At least one progress toward goal must be selected");
      errorSetters.progressTowardGoalsError = "At least one progress toward goal must be selected";
    }

    if (!sessionFormData.barriersToProgress || sessionFormData.barriersToProgress.length === 0) {
      errors.push("At least one barrier to progress must be selected");
      errorSetters.barriersToProgressError = "At least one barrier to progress must be selected";
    }

    // Care Plan - All fields are required
    if (!sessionFormData.clientGoal || sessionFormData.clientGoal.trim() === "") {
      errors.push("Client Goal is required");
      errorSetters.clientGoalError = "Client Goal is required";
    }

    if (!sessionFormData.objectives || sessionFormData.objectives.trim() === "") {
      errors.push("Objectives is required");
      errorSetters.objectivesError = "Objectives is required";
    }

    if (!sessionFormData.interventions || sessionFormData.interventions.trim() === "") {
      errors.push("Interventions is required");
      errorSetters.interventionsError = "Interventions is required";
    }

    if (!sessionFormData.sessionFrequency || sessionFormData.sessionFrequency === "") {
      errors.push("Session Frequency is required");
      errorSetters.sessionFrequencyError = "Session Frequency is required";
    }

    if (sessionFormData.sessionFrequency === "Other" && (!sessionFormData.otherSessionFrequencyText || sessionFormData.otherSessionFrequencyText.trim() === "")) {
      errors.push("Please specify frequency");
      errorSetters.sessionFrequencyError = "Please specify frequency";
    }

    if (!sessionFormData.reviewDate || sessionFormData.reviewDate === "") {
      errors.push("Review Date is required");
      errorSetters.reviewDateError = "Review Date is required";
    }

    // Set all error states using helper function
    setErrorStates(errorSetters);

    // Show all error toasts
    if (errors.length > 0) {
      errors.forEach((error, index) => {
        setTimeout(() => {
          toast.error(error);
        }, index * 300); // Stagger toasts by 300ms
      });
      return;
    }

    try {
      setSigningAndSubmitting(true);
      const payload = buildSavePayload(false); // isAutoSave: false for submit
      payload.isSubmit = true; // Mark as submitted
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/createSessionHistory`,
        payload,
        getConfigWithToken(user.user?.token)
      );
      if (response.data.success) {
        setIsSubmit(true);
        clearAllErrors(); // Clear all errors on successful sign and submit
        toast.success("Follow-up signed and submitted successfully!", {
          position: "bottom-right",
          autoClose: 3000,
        });
        // Reset unsaved changes since we just submitted
        setHasUnsavedChanges(false);
        setLastSavedTimestamp(new Date());
      } else {
        // Show all validation errors from backend
        if (response.data.errors && Array.isArray(response.data.errors)) {
          response.data.errors.forEach((error, index) => {
            setTimeout(() => {
              toast.error(error);
            }, index * 300);
          });
        } else {
          toast.error(response.data.message || "Failed to sign and submit follow-up");
        }
      }
    } catch (error) {
      console.error("Error signing and submitting follow-up:", error);
      // Show all validation errors from backend
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        error.response.data.errors.forEach((err, index) => {
          setTimeout(() => {
            toast.error(err);
          }, index * 300);
        });
      } else {
        toast.error(
          error.response?.data?.message ||
          "An error occurred while signing and submitting follow-up"
        );
      }
    } finally {
      setSigningAndSubmitting(false);
    }
  };

  // Update form data function
  const updateFormData = (fieldName, value) => {
    setSessionFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    setHasUnsavedChanges(true);
  };

  // Time picker helper functions
  const getDefaultTimeParts = () => {
    const now = new Date();
    let hours = now.getHours();
    const period = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return {
      hour: hours.toString().padStart(2, "0"),
      minute: now.getMinutes().toString().padStart(2, "0"),
      period
    };
  };

  const formatTimeFromParts = (parts) => {
    if (!parts.hour || !parts.minute || !parts.period) {
      return "";
    }
    const hourNumber = parseInt(parts.hour, 10);
    if (Number.isNaN(hourNumber) || hourNumber < 1 || hourNumber > 12) {
      return "";
    }
    const minuteValue = parts.minute.padStart(2, "0");
    const periodValue = parts.period.toUpperCase();
    const hourDisplay = hourNumber.toString().padStart(2, "0");
    return `${hourDisplay}:${minuteValue} ${periodValue}`;
  };

  const parseTimeString = useCallback((value = "") => {
    if (!value) return null;
    const trimmedValue = value.trim().toUpperCase();
    const match = trimmedValue.match(/^(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM)$/);
    if (!match) return null;
    let hourNumber = parseInt(match[1], 10);
    if (Number.isNaN(hourNumber) || hourNumber < 1) {
      hourNumber = 1;
    } else if (hourNumber > 12) {
      hourNumber = 12;
    }
    let minuteNumber = match[2] !== undefined ? parseInt(match[2], 10) : 0;
    if (Number.isNaN(minuteNumber) || minuteNumber < 0) {
      minuteNumber = 0;
    } else if (minuteNumber > 59) {
      minuteNumber = 59;
    }
    return {
      hour: hourNumber.toString().padStart(2, "0"),
      minute: minuteNumber.toString().padStart(2, "0"),
      period: match[3]
    };
  }, []);

  const getMinutesFromTimeString = useCallback(
    (value) => {
      const parsed = parseTimeString(value);
      if (!parsed) return null;

      const hour = parseInt(parsed.hour, 10);
      const minute = parseInt(parsed.minute, 10);
      if (Number.isNaN(hour) || Number.isNaN(minute)) {
        return null;
      }

      let hour24 = hour % 12;
      if (parsed.period === "PM") {
        hour24 += 12;
      } else if (parsed.period === "AM" && hour === 12) {
        hour24 = 0;
      }

      return hour24 * 60 + minute;
    },
    [parseTimeString]
  );

  const ensureTimeParts = (parts) => {
    const defaults = getDefaultTimeParts();
    return {
      hour: parts.hour || defaults.hour,
      minute: parts.minute || defaults.minute,
      period: parts.period || defaults.period
    };
  };

  const markTimeFieldUpdate = (fieldName) => {
    if (fieldName === "timeIn") {
      timeInUpdateFromInputRef.current = true;
    } else if (fieldName === "timeOut") {
      timeOutUpdateFromInputRef.current = true;
    }
  };

  const handleTimeSegmentChange = (fieldName, setParts, segment, value) => {
    setParts((prev) => {
      const updated = { ...prev };
      if (segment === "hour") {
        if (!value) {
          updated.hour = "";
        } else {
          const digits = value.replace(/\D/g, "");
          if (!digits) {
            updated.hour = "";
          } else if (digits.length === 1) {
            const firstDigit = digits[0];
            if (firstDigit === "0") {
              updated.hour = "0";
            } else if (firstDigit === "1") {
              updated.hour = "1";
            } else {
              const hourNumber = parseInt(firstDigit, 10);
              if (hourNumber >= 2 && hourNumber <= 9) {
                updated.hour = hourNumber.toString().padStart(2, "0");
              } else {
                updated.hour = "01";
              }
            }
          } else {
            const normalizedDigits = digits.slice(-2);
            let hourNumber = parseInt(normalizedDigits, 10);
            if (normalizedDigits === "00") {
              hourNumber = 12;
              updated.hour = "12";
            } else if (Number.isNaN(hourNumber)) {
              updated.hour = "1";
            } else if (hourNumber < 1) {
              updated.hour = "1";
            } else if (hourNumber > 12) {
              updated.hour = "12";
            } else {
              if (normalizedDigits[0] === "0") {
                const secondDigit = parseInt(normalizedDigits[1], 10);
                if (secondDigit >= 1 && secondDigit <= 9) {
                  updated.hour = normalizedDigits;
                } else {
                  updated.hour = prev.hour || "1";
                }
              } else if (hourNumber === 1) {
                updated.hour = hourNumber.toString().padStart(2, "0");
              } else {
                updated.hour = hourNumber.toString().padStart(2, "0");
              }
            }
          }
        }
      } else if (segment === "minute") {
        if (!value) {
          updated.minute = "";
        } else {
          const digits = value.replace(/\D/g, "");
          if (!digits) {
            updated.minute = "";
          } else if (digits.length === 1) {
            updated.minute = digits;
          } else {
            const normalizedDigits = digits.slice(-2);
            let minuteNumber = parseInt(normalizedDigits, 10);
            if (Number.isNaN(minuteNumber) || minuteNumber < 0) {
              minuteNumber = 0;
            } else if (minuteNumber > 59) {
              minuteNumber = 59;
            }
            updated.minute = minuteNumber.toString().padStart(2, "0");
          }
        }
      } else if (segment === "period") {
        if (!value) {
          updated.period = "";
        } else {
          const letters = value.replace(/[^aApPmM]/g, "").toUpperCase();
          if (!letters) {
            updated.period = "";
          } else if (letters.startsWith("A")) {
            updated.period = "AM";
          } else if (letters.startsWith("P")) {
            updated.period = "PM";
          }
        }
      }
      const formatted = formatTimeFromParts(updated);
      if (formatted) {
        markTimeFieldUpdate(fieldName);
        updateFormData(fieldName, formatted);
      } else if (!updated.hour && !updated.minute && !updated.period) {
        markTimeFieldUpdate(fieldName);
        updateFormData(fieldName, "");
      }
      return updated;
    });
  };

  const handleTimeSegmentBlur = (fieldName, setParts, segment) => {
    setParts((prev) => {
      const updated = { ...prev };
      const formattedParts = { ...updated };
      if (segment === "hour" && formattedParts.hour && formattedParts.hour.length === 1) {
        formattedParts.hour = formattedParts.hour.padStart(2, "0");
      }
      if (segment === "minute" && formattedParts.minute && formattedParts.minute.length === 1) {
        formattedParts.minute = formattedParts.minute.padStart(2, "0");
      }
      const formatted = formatTimeFromParts(formattedParts);
      if (formatted) {
        markTimeFieldUpdate(fieldName);
        updateFormData(fieldName, formatted);
      }
      return updated;
    });
  };

  const handleTimeInputFocus = (setPickerOpen) => {
    setPickerOpen(true);
  };

  const handleHourAdjust = (fieldName, setParts, direction) => {
    setParts((prev) => {
      const base = ensureTimeParts(prev);
      let hourValue = parseInt(base.hour, 10);
      if (Number.isNaN(hourValue) || hourValue < 1 || hourValue > 12) {
        hourValue = 12;
      }
      hourValue = ((hourValue - 1 + direction + 12) % 12) + 1;
      const updated = { ...base, hour: hourValue.toString().padStart(2, "0") };
      markTimeFieldUpdate(fieldName);
      updateFormData(fieldName, formatTimeFromParts(updated));
      return updated;
    });
  };

  const handleMinuteAdjust = (fieldName, setParts, direction) => {
    setParts((prev) => {
      const base = ensureTimeParts(prev);
      let minuteValue = parseInt(base.minute, 10);
      if (Number.isNaN(minuteValue) || minuteValue < 0 || minuteValue > 59) {
        minuteValue = 0;
      }
      minuteValue = (minuteValue + direction + 60) % 60;
      const updated = { ...base, minute: minuteValue.toString().padStart(2, "0") };
      markTimeFieldUpdate(fieldName);
      updateFormData(fieldName, formatTimeFromParts(updated));
      return updated;
    });
  };

  const handlePeriodToggle = (fieldName, setParts) => {
    setParts((prev) => {
      const base = ensureTimeParts(prev);
      const updated = { ...base, period: base.period === "AM" ? "PM" : "AM" };
      markTimeFieldUpdate(fieldName);
      updateFormData(fieldName, formatTimeFromParts(updated));
      return updated;
    });
  };

  // Auto resize textarea function
  const autoResizeTextarea = (element, respectMinHeight = false) => {
    if (!element) return;
    if (respectMinHeight) {
      let minHeight = 72;
      if (element.style.minHeight) {
        const minHeightStr = element.style.minHeight;
        minHeight = parseInt(minHeightStr) || 72;
      }

      const hasValue = element.value && element.value.trim().length > 0;

      if (!hasValue) {
        element.style.height = `${minHeight}px`;
        return;
      }

      const calculateHeight = () => {
        element.style.height = "auto";
        void element.offsetHeight;
        const scrollHeight = element.scrollHeight;
        const newHeight = Math.max(minHeight, scrollHeight);
        element.style.height = `${newHeight}px`;
      };

      calculateHeight();
      requestAnimationFrame(() => {
        if (element && element.value) {
          calculateHeight();
        }
      });
    } else {
      element.style.height = "auto";
      element.style.height = `${element.scrollHeight}px`;
    }
  };

  // Calculate duration display
  const derivedDurationMinutes = useMemo(() => {
    const startMinutes = getMinutesFromTimeString(sessionFormData.timeIn);
    const endMinutes = getMinutesFromTimeString(sessionFormData.timeOut);

    if (startMinutes === null || endMinutes === null) {
      return null;
    }

    let diff = endMinutes - startMinutes;
    if (diff < 0) {
      diff += 24 * 60;
    }
    return diff;
  }, [sessionFormData.timeIn, sessionFormData.timeOut, getMinutesFromTimeString]);

  const durationDisplay = derivedDurationMinutes !== null ? `${derivedDurationMinutes}m` : "";

  // Build save payload
  const buildSavePayload = useCallback((isAutoSave = false) => {
    let totalDuration = null;

    const startMinutes = getMinutesFromTimeString(sessionFormData.timeIn);
    const endMinutes = getMinutesFromTimeString(sessionFormData.timeOut);

    if (startMinutes !== null && endMinutes !== null) {
      let diff = endMinutes - startMinutes;
      if (diff < 0) {
        diff += 24 * 60;
      }
      if (diff > 0) {
        totalDuration = diff;
      }
    }

    return {
      sessionId: session._id,
      isAutoSave: isAutoSave,
      isFollowUp: true, // Flag to indicate this is from follow-up page
      // Appointment Details
      timeIn: sessionFormData.timeIn || "",
      timeOut: sessionFormData.timeOut || "",
      wasClientAtHome: sessionFormData.wasClientAtHome || "",
      totalDuration: totalDuration,
      // Diagnosis fields
      primaryDiagnosis: sessionFormData.primaryDiagnosis || "",
      primaryDiagnosisDescription: sessionFormData.primaryDiagnosisDescription || "",
      secondaryDiagnosis: sessionFormData.secondaryDiagnosis || "",
      secondaryDiagnosisDescription: sessionFormData.secondaryDiagnosisDescription || "",
      tertiaryDiagnosis: sessionFormData.tertiaryDiagnosis || "",
      tertiaryDiagnosisDescription: sessionFormData.tertiaryDiagnosisDescription || "",
      // Client Presentation fields
      focusOfSession: sessionFormData.focusOfSession || "",
      selectedSymptoms: sessionFormData.selectedSymptoms || [],
      otherSymptomText: sessionFormData.otherSymptomText || "",
      moodAffect: sessionFormData.moodAffect || "",
      otherMoodAffectText: sessionFormData.otherMoodAffectText || "",
      behaviorEngagement: sessionFormData.behaviorEngagement || "",
      otherBehaviorEngagementText: sessionFormData.otherBehaviorEngagementText || "",
      // Interventions fields
      interventionsUsed: sessionFormData.interventionsUsed || "",
      // Client Progress fields
      clientResponseToCare: sessionFormData.clientResponseToCare || "",
      progressTowardGoals: sessionFormData.progressTowardGoals || [],
      goalMetDescription: sessionFormData.goalMetDescription || "",
      barriersToProgress: sessionFormData.barriersToProgress || [],
      otherBarrierText: sessionFormData.otherBarrierText || "",
      additionalNotes: sessionFormData.additionalNotes || "",
      // Care Plan fields
      clientGoal: sessionFormData.clientGoal || "",
      objectives: sessionFormData.objectives || "",
      interventions: sessionFormData.interventions || "",
      sessionFrequency: sessionFormData.sessionFrequency || "",
      otherSessionFrequencyText: sessionFormData.otherSessionFrequencyText || "",
      reviewDate: sessionFormData.reviewDate || "",
      // Preserve isSigned from database - never send false if it was true in DB
      // Only allow setting to true explicitly, never reset to false
      isSigned: isSignedFromDB || sessionFormData.isSigned || false,
      addendumData: (() => {
        // If addAddendum is true, handle addendumData array
        if (addAddendum && sessionFormData.addendumText) {
          const updatedAddendumData = [...addendumData];

          // If addendumData is empty, create new entry
          if (!updatedAddendumData || updatedAddendumData.length === 0) {
            updatedAddendumData.push({
              addendumText: sessionFormData.addendumText,
              addendumSigned: false,
              addendumSubmit: false,
              addendumSignedAt: null,
              createdAt: new Date().toISOString()
            });
          } else {
            // Get latest entry
            const lastIndex = updatedAddendumData.length - 1;
            const lastAddendum = updatedAddendumData[lastIndex];

            // If latest entry's addendumSigned is true, create new entry
            if (lastAddendum && lastAddendum.addendumSigned === true) {
              updatedAddendumData.push({
                addendumText: sessionFormData.addendumText,
                addendumSigned: false,
                addendumSubmit: false,
                addendumSignedAt: null,
                createdAt: new Date().toISOString()
              });
            }
            // If latest entry's addendumSigned is false, update that entry
            else if (lastAddendum && lastAddendum.addendumSigned === false) {
              updatedAddendumData[lastIndex] = {
                ...updatedAddendumData[lastIndex],
                addendumText: sessionFormData.addendumText,
                addendumSigned: false,
                // Preserve existing addendumSubmit value
                addendumSubmit: updatedAddendumData[lastIndex].addendumSubmit ?? false,
                addendumSignedAt: null,
                updatedAt: new Date().toISOString()
              };
            }
            // Fallback: create new entry
            else {
              updatedAddendumData.push({
                addendumText: sessionFormData.addendumText,
                addendumSigned: false,
                addendumSubmit: false,
                addendumSignedAt: null,
                createdAt: new Date().toISOString()
              });
            }
          }
          // Process the array to ensure all entries have addendumSignedAt field properly set
          return processAddendumData(updatedAddendumData);
        }
        // If addAddendum is false, preserve existing addendumData
        // Signed entries (addendumSigned === true) are locked and will be preserved as-is
        return processAddendumData(addendumData);
      })(),
    };
  }, [session?._id, sessionFormData, getMinutesFromTimeString, isSignedFromDB, addAddendum, addendumData, currentAddendumIndex, processAddendumData, carePlanEditMode]);

  // Auto-save function
  const handleAutoSave = useCallback(async (showIndicator = false) => {
    if (!session?._id || !hasUnsavedChanges) {
      return; // No unsaved changes, skip auto-save
    }

    if (autoSaving || saving) {
      return; // Already saving, skip
    }

    if (!dataLoaded) {
      return;
    }

    try {
      if (showIndicator) {
        setAutoSaving(true);
      }

      const payload = buildSavePayload(true); // Pass isAutoSave: true
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/createSessionHistory`,
        payload,
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success) {
        const currentData = JSON.stringify(payload);
        setLastSavedTimestamp(new Date());
        setHasUnsavedChanges(false);
        setLastSavedData(currentData);

        // Update addendumData state if addAddendum is true
        if (addAddendum && payload.addendumData) {
          setAddendumData(processAddendumData(payload.addendumData));
        }

        clearAllErrors(); // Clear all errors on successful auto-save
        errorToastShownRef.current = false; // Reset error toast state on successful save
        toast.dismiss("auto-save-error"); // Dismiss error toast if it exists

        // Clear retry timeout if exists
        if (retryTimeoutId) {
          clearTimeout(retryTimeoutId);
          setRetryTimeoutId(null);
        }

        if (showIndicator) {
          toast.success("💾 Progress saved.", {
            position: "bottom-right",
            autoClose: 2000,
            hideProgressBar: true,
          });
        }
      }
    } catch (error) {
      console.error("Auto-save failed:", error);

      // Show error banner only once
      if (!errorToastShownRef.current) {
        toast.error("⚠️ Unable to save changes — retrying automatically.", {
          position: "bottom-right",
          autoClose: false,
          hideProgressBar: false,
          toastId: "auto-save-error", // Use same toastId to prevent duplicates
        });
        errorToastShownRef.current = true;
      }

      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }

      // Retry after 15 seconds
      const timeoutId = setTimeout(() => {
        handleAutoSave(false);
      }, 15000);
      setRetryTimeoutId(timeoutId);
    } finally {
      if (showIndicator) {
        setAutoSaving(false);
      }
    }
  }, [session?._id, hasUnsavedChanges, autoSaving, saving, buildSavePayload, user?.user?.token, retryTimeoutId, dataLoaded, addAddendum, processAddendumData]);

  // Sync time parts with form data
  useEffect(() => {
    if (timeInUpdateFromInputRef.current) {
      timeInUpdateFromInputRef.current = false;
      return;
    }
    if (!sessionFormData.timeIn) {
      setTimeInParts({ hour: "", minute: "", period: "" });
      return;
    }
    const parsed = parseTimeString(sessionFormData.timeIn);
    if (parsed) {
      setTimeInParts(parsed);
    }
  }, [sessionFormData.timeIn, parseTimeString]);

  useEffect(() => {
    if (timeOutUpdateFromInputRef.current) {
      timeOutUpdateFromInputRef.current = false;
      return;
    }
    if (!sessionFormData.timeOut) {
      setTimeOutParts({ hour: "", minute: "", period: "" });
      return;
    }
    const parsed = parseTimeString(sessionFormData.timeOut);
    if (parsed) {
      setTimeOutParts(parsed);
    }
  }, [sessionFormData.timeOut, parseTimeString]);

  // Handle click outside for time pickers
  useEffect(() => {
    if (!isTimeInPickerOpen) return;
    const handleClickOutside = (event) => {
      if (timeInPickerRef.current && !timeInPickerRef.current.contains(event.target)) {
        setIsTimeInPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isTimeInPickerOpen]);

  useEffect(() => {
    if (!isTimeOutPickerOpen) return;
    const handleClickOutside = (event) => {
      if (timeOutPickerRef.current && !timeOutPickerRef.current.contains(event.target)) {
        setIsTimeOutPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isTimeOutPickerOpen]);

  // Fetch follow-up data from API - same as SessionHistoryDetailView
  const fetchFollowUpData = useCallback(async () => {
    if (!session?._id) {
      setLoading(false);
      setDataLoaded(true);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/get-by-session/${session._id}`,
        getConfigWithToken(user?.user?.token)
      );

      if (response.data.success && response.data.data) {
        const existingData = response.data.data;

        // Note: Care Plan and Diagnosis data come from user API, NOT from session data
        // Only store original care plan data if not already set from user API
        if (!originalCarePlanData) {
          const carePlanData = {
            clientGoal: sessionFormData.clientGoal || "",
            objectives: sessionFormData.objectives || "",
            interventions: sessionFormData.interventions || "",
            sessionFrequency: sessionFormData.sessionFrequency || "",
            otherSessionFrequencyText: sessionFormData.otherSessionFrequencyText || "",
            reviewDate: sessionFormData.reviewDate || "",
          };
          setOriginalCarePlanData(carePlanData);
        }

        // Update form data with fetched values - matching SessionHistoryDetailView pattern
        // Note: Diagnosis and Care Plan come from user API, NOT from session data
        setSessionFormData(prev => ({
          ...prev,
          // Care Plan fields - DO NOT use session data, preserve user API data
          // (user API data takes priority)
          clientGoal: prev.clientGoal || "",
          objectives: prev.objectives || "",
          interventions: prev.interventions || "",
          sessionFrequency: prev.sessionFrequency || "",
          otherSessionFrequencyText: prev.otherSessionFrequencyText || "",
          reviewDate: prev.reviewDate || "",
          // Appointment Details fields
          timeIn: existingData.timeIn || "",
          timeOut: existingData.timeOut || "",
          wasClientAtHome: existingData.wasClientAtHome || "",
          // Diagnosis fields - DO NOT use session data, only use if not already set from user API
          // (user API data takes priority)
          primaryDiagnosis: prev.primaryDiagnosis || "",
          primaryDiagnosisDescription: prev.primaryDiagnosisDescription || "",
          secondaryDiagnosis: prev.secondaryDiagnosis || "",
          secondaryDiagnosisDescription: prev.secondaryDiagnosisDescription || "",
          tertiaryDiagnosis: prev.tertiaryDiagnosis || "",
          tertiaryDiagnosisDescription: prev.tertiaryDiagnosisDescription || "",
          // Client Presentation fields - make these dynamic
          focusOfSession: existingData.focusOfSession || "",
          selectedSymptoms: existingData.selectedSymptoms || [],
          otherSymptomText: existingData.otherSymptomText || "",
          moodAffect: existingData.moodAffect || "",
          otherMoodAffectText: existingData.otherMoodAffectText || "",
          behaviorEngagement: existingData.behaviorEngagement || "",
          otherBehaviorEngagementText: existingData.otherBehaviorEngagementText || "",
          // Interventions fields - make these dynamic
          interventionsUsed: existingData.interventionsUsed || "",
          // Client Progress fields - make these dynamic
          clientResponseToCare: existingData.clientResponseToCare || "",
          progressTowardGoals: existingData.progressTowardGoals || [],
          goalMetDescription: existingData.goalMetDescription || "",
          barriersToProgress: existingData.barriersToProgress || [],
          otherBarrierText: existingData.otherBarrierText || "",
          additionalNotes: existingData.additionalNotes || "",
          signedAt: existingData.signedAt || null,
          isSigned: prev.isSigned || existingData.isSigned || false
        }));

        // Preserve isSigned state - only update if database has true, never reset to false
        const dbIsSigned = existingData.isSigned === true;
        setIsSigned(prev => prev || dbIsSigned);
        setIsSignedFromDB(dbIsSigned); // Store the database value

        // Set default review date if not present (180 days from today)
        if (!existingData.reviewDate || existingData.reviewDate === "") {
          const today = new Date();
          const reviewDate = new Date(today);
          reviewDate.setDate(today.getDate() + 180);
          const formattedDate = reviewDate.toISOString().split('T')[0];
          setSessionFormData(prev => ({
            ...prev,
            reviewDate: formattedDate
          }));
        }

        // Set submission state from database
        if (existingData.isSubmit !== undefined) {
          setIsSubmit(existingData.isSubmit);
        }

        // Fetch and set addendumData
        if (existingData.addendumData && Array.isArray(existingData.addendumData)) {
          const processedAddendumData = processAddendumData(existingData.addendumData);
          setAddendumData(processedAddendumData);

          // Update addendumText from latest addendum if it can be edited
          if (processedAddendumData && processedAddendumData.length > 0) {
            const latestIndex = processedAddendumData.length - 1;
            const latestAddendum = processedAddendumData[latestIndex];

            // If latest addendum can be edited (addendumSigned: false, addendumSubmit: true)
            if (latestAddendum &&
              latestAddendum.addendumSigned === false &&
              latestAddendum.addendumSubmit === true) {
              setSessionFormData(prev => ({
                ...prev,
                addendumText: latestAddendum.addendumText || ""
              }));
              setCurrentAddendumIndex(latestIndex);
            }
          }
        } else {
          setAddendumData([]);
        }

        setHasUnsavedChanges(false);
        setDataLoaded(true);
      } else {
        // No existing data - set defaults
        setIsSubmit(false); // New records should have isSubmit as false
        setAddendumData([]); // Initialize addendumData as empty array
        setHasUnsavedChanges(false);
        setDataLoaded(true);
        // Set default review date (180 days from today)
        const today = new Date();
        const reviewDate = new Date(today);
        reviewDate.setDate(today.getDate() + 180);
        const formattedDate = reviewDate.toISOString().split('T')[0];
        setSessionFormData(prev => ({
          ...prev,
          reviewDate: formattedDate
        }));
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error("Error fetching follow-up data:", error);
        toast.error("Unable to load follow-up data. Please try again.");
      } else {
        // 404 means no data exists - set defaults
        setIsSubmit(false); // New records should have isSubmit as false
        setAddendumData([]); // Initialize addendumData as empty array
      }
      // Set defaults even on error
      setHasUnsavedChanges(false);
      setDataLoaded(true);
      // Set default review date (180 days from today)
      const today = new Date();
      const reviewDate = new Date(today);
      reviewDate.setDate(today.getDate() + 180);
      const formattedDate = reviewDate.toISOString().split('T')[0];
      setSessionFormData(prev => ({
        ...prev,
        reviewDate: formattedDate
      }));
    } finally {
      setLoading(false);
    }
  }, [session?._id, user?.user?.token]);

  // Fetch diagnosis and care plans from user API
  const fetchUserDiagnosisAndCarePlans = useCallback(async () => {
    // Get user ID from session.user_fk or session.user._id
    const userId = session?.user_fk || session?.user?._id;
    if (!userId) {
      return;
    }

    try {
      setLoadingDiagnosisAndCarePlans(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/get-diagnosis-and-care-plans/${userId}`,
        getConfigWithToken(user?.user?.token)
      );
      if (response.data.success && response.data.data) {
        setUserDiagnosisAndCarePlans(response.data.data);

        // Auto-populate diagnosis fields from active diagnosis
        const activeDiagnoses = response.data.data.diagnosis?.active || [];
        if (activeDiagnoses.length > 0) {
          setSessionFormData(prev => ({
            ...prev,
            primaryDiagnosis: activeDiagnoses[0]?.code || "",
            primaryDiagnosisDescription: activeDiagnoses[0]?.description || "",
            secondaryDiagnosis: activeDiagnoses[1]?.code || "",
            secondaryDiagnosisDescription: activeDiagnoses[1]?.description || "",
            tertiaryDiagnosis: activeDiagnoses[2]?.code || "",
            tertiaryDiagnosisDescription: activeDiagnoses[2]?.description || "",
          }));
        }

        // Auto-populate care plan fields from active care plan
        const activeCarePlan = response.data.data.carePlans?.active;
        if (activeCarePlan) {
          const carePlanData = {
            clientGoal: activeCarePlan.clientGoal || "",
            objectives: activeCarePlan.objectives || "",
            interventions: activeCarePlan.interventions || "",
            sessionFrequency: activeCarePlan.frequency || activeCarePlan.sessionFrequency || "",
            otherSessionFrequencyText: activeCarePlan.otherSessionFrequencyText || "",
            reviewDate: activeCarePlan.reviewDate || "",
          };

          setSessionFormData(prev => ({
            ...prev,
            ...carePlanData,
          }));

          // Store as original care plan data
          setOriginalCarePlanData(carePlanData);
        }
      }
    } catch (error) {
      console.error("Error fetching user diagnosis and care plans:", error);
      // Don't show error toast, just log it
    } finally {
      setLoadingDiagnosisAndCarePlans(false);
    }
  }, [session?.user_fk, session?.user?._id, user?.user?.token]);

  // Fetch data on component mount - same pattern as SessionHistoryDetailView
  useEffect(() => {
    if (session?._id) {
      fetchFollowUpData();
    }
  }, [session?._id, fetchFollowUpData]);

  // Fetch user diagnosis and care plans when session is available
  useEffect(() => {
    const userId = session?.user_fk || session?.user?._id;
    if (userId) {
      fetchUserDiagnosisAndCarePlans();
    }
  }, [session?.user_fk, session?.user?._id, fetchUserDiagnosisAndCarePlans]);

  // Helper function to clear all errors
  const clearAllErrors = () => {
    setClientGoalError("");
    setObjectivesError("");
    setInterventionsError("");
    setSessionFrequencyError("");
    setReviewDateError("");
    setClientResponseToCareError("");
    setProgressTowardGoalsError("");
    setBarriersToProgressError("");
    setFocusOfSessionError("");
    setSymptomsError("");
    setMoodAffectError("");
    setBehaviorEngagementError("");
    setPrimaryDiagnosisError("");
    setInterventionsUsedError("");
    setTimeInError("");
    setTimeOutError("");
    setWasClientAtHomeError("");
    setGoalMetDescriptionError("");
    setAdditionalNotesError("");
  };

  // Error state setters mapping for helper functions
  const errorSettersMap = {
    clientGoalError: setClientGoalError,
    objectivesError: setObjectivesError,
    interventionsError: setInterventionsError,
    sessionFrequencyError: setSessionFrequencyError,
    reviewDateError: setReviewDateError,
    clientResponseToCareError: setClientResponseToCareError,
    progressTowardGoalsError: setProgressTowardGoalsError,
    barriersToProgressError: setBarriersToProgressError,
    focusOfSessionError: setFocusOfSessionError,
    symptomsError: setSymptomsError,
    moodAffectError: setMoodAffectError,
    behaviorEngagementError: setBehaviorEngagementError,
    primaryDiagnosisError: setPrimaryDiagnosisError,
    interventionsUsedError: setInterventionsUsedError,
    timeInError: setTimeInError,
    timeOutError: setTimeOutError,
    wasClientAtHomeError: setWasClientAtHomeError,
    goalMetDescriptionError: setGoalMetDescriptionError,
    additionalNotesError: setAdditionalNotesError,
  };

  // Helper function to set error states from errorSetters object
  const setErrorStates = (errorSetters) => {
    Object.keys(errorSetters).forEach((errorKey) => {
      if (errorSetters[errorKey] && errorSettersMap[errorKey]) {
        errorSettersMap[errorKey](errorSetters[errorKey]);
      }
    });
  };

  // Auto-save on interval - every 30 seconds
  useEffect(() => {
    if (!session?._id || !hasUnsavedChanges || !dataLoaded) {
      return;
    }

    const interval = setInterval(() => {
      handleAutoSave(false); // Auto-save without showing indicator
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [hasUnsavedChanges, session?._id, handleAutoSave, dataLoaded]);

  // Auto-save on section change
  useEffect(() => {
    if (activeSection && hasUnsavedChanges && dataLoaded) {
      handleAutoSave(false);
    }
  }, [activeSection, hasUnsavedChanges, handleAutoSave, dataLoaded]);

  // Auto-save on beforeunload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && dataLoaded) {
        handleAutoSave(true); // Show "Saving progress..." indicator
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Saving progress...";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges, handleAutoSave, dataLoaded]);

  // Auto-save when checkboxes change (selectedSymptoms)
  useEffect(() => {
    if (dataLoaded && hasUnsavedChanges && session?._id) {
      // Debounce auto-save for checkbox changes (500ms delay)
      const timeoutId = setTimeout(() => {
        handleAutoSave(false);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [sessionFormData.selectedSymptoms, dataLoaded, hasUnsavedChanges, session?._id, handleAutoSave]);

  // Auto-save when checkboxes change (progressTowardGoals)
  useEffect(() => {
    if (dataLoaded && hasUnsavedChanges && session?._id) {
      // Debounce auto-save for checkbox changes (500ms delay)
      const timeoutId = setTimeout(() => {
        handleAutoSave(false);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [sessionFormData.progressTowardGoals, dataLoaded, hasUnsavedChanges, session?._id, handleAutoSave]);

  // Auto-save when checkboxes change (barriersToProgress)
  useEffect(() => {
    if (dataLoaded && hasUnsavedChanges && session?._id) {
      // Debounce auto-save for checkbox changes (500ms delay)
      const timeoutId = setTimeout(() => {
        handleAutoSave(false);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [sessionFormData.barriersToProgress, dataLoaded, hasUnsavedChanges, session?._id, handleAutoSave]);

  // Initialize addendumText based on addendumData when addAddendum becomes true
  useEffect(() => {
    // Reset initialization flag when addAddendum becomes false
    if (!addAddendum) {
      addendumInitializedRef.current = false;
      return;
    }

    // Only initialize once when addAddendum first becomes true and data is loaded
    if (addAddendum && dataLoaded && !addendumInitializedRef.current) {
      // If addendumData is empty or null, keep textarea empty
      if (!addendumData || addendumData.length === 0) {
        setSessionFormData(prev => ({ ...prev, addendumText: "" }));
        setCurrentAddendumIndex(null);
        addendumInitializedRef.current = true;
        return;
      }

      // Find latest/last object
      const lastAddendum = addendumData[addendumData.length - 1];
      const lastIndex = addendumData.length - 1;

      // If last object's addendumSigned is true, keep textarea empty (new entry will be created)
      if (lastAddendum && lastAddendum.addendumSigned === true) {
        setSessionFormData(prev => ({ ...prev, addendumText: "" }));
        setCurrentAddendumIndex(null);
      }
      // If last object's addendumSigned is false, show its addendumText
      else if (lastAddendum && lastAddendum.addendumSigned === false) {
        setSessionFormData(prev => ({ ...prev, addendumText: lastAddendum.addendumText || "" }));
        setCurrentAddendumIndex(lastIndex);
      } else {
        setSessionFormData(prev => ({ ...prev, addendumText: "" }));
        setCurrentAddendumIndex(null);
      }
      addendumInitializedRef.current = true;
    }
  }, [addAddendum, addendumData, dataLoaded]);

  // Initialize addAddendum from location.state if openAddendum is true
  useEffect(() => {
    if (openAddendum === true) {
      setAddAddendum(true);
    }
  }, [openAddendum]);

  useEffect(() => {
    if (addAddendum && sessionFormData.addendumText) {
      autoResizeTextarea(document.getElementById("addendum-textarea"));
    }
  }, [addAddendum, sessionFormData.addendumText]);

  // Toggle functions for expandable sections
  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => {
      if (prev.includes(sectionId)) {
        return prev.filter((id) => id !== sectionId);
      }
      return [...prev, sectionId];
    });
  };

  const expandSection = (sectionId) => {
    setExpandedSections((prev) => {
      if (prev.includes(sectionId)) {
        return prev;
      }
      return [...prev, sectionId];
    });
  };

  const collapseSection = (sectionId) => {
    setExpandedSections((prev) => prev.filter((id) => id !== sectionId));
  };

  // Reset expanded sections when session changes
  useEffect(() => {
    setExpandedSections([]);
  }, [session?._id]);

  // Format time string helper
  const formatTimeString = (timeString) => {
    if (!timeString) return "";
    return timeString;
  };

  // Capitalize first character function
  const capitalizeFirstChar = (str) => {
    if (!str || typeof str !== 'string') return str || "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Function to calculate addendum number based on addendumData
  const getAddendumNumber = () => {
    // If addendumData array is empty, show "Addendum # 1"
    if (!addendumData || addendumData.length === 0) {
      return "Addendum # 1";
    }

    // If array has objects, check the last object
    const lastAddendum = addendumData[addendumData.length - 1];
    const arrayLength = addendumData.length;

    // If latest object's addendumSigned is false and addendumSubmit is true, show length
    if (lastAddendum && lastAddendum.addendumSigned === false && lastAddendum.addendumSubmit === true) {
      return `Addendum # ${arrayLength}`;
    }

    // If last object's addendumSigned is true, show length + 1
    if (lastAddendum && lastAddendum.addendumSigned === true) {
      return `Addendum # ${arrayLength + 1}`;
    }

    // If last object's addendumSigned is false, just show length
    return `Addendum # ${arrayLength}`;
  };

  // Sections for sidebar navigation
  const sections = [
    { id: "appointment-details", label: "Appointment Details" },
    { id: "client-presentation", label: "Client Presentation" },
    { id: "interventions", label: "Interventions" },
    { id: "client-progress", label: "Client Progress" },
    { id: "care-plan", label: "Care Plan" },
  ];

  // Add addendum section if there's an addendum to show
  if (addAddendum === true || canEditAddendum() || (isSubmit && addendumData && Array.isArray(addendumData) && addendumData.length > 0)) {
    sections.push({
      id: "addendum",
      label: "Addendum / Correction"
    });
  }

  // Scroll to section function
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);

    // List of expandable section IDs in read-only view
    const expandableSectionIds = [
      "appointment-details",
      "client-presentation",
      "interventions",
      "client-progress",
      "care-plan"
    ];

    // If in read-only view (signed or submitted), toggle expand/collapse
    if (isSubmit) {
      // Check if section is expandable
      if (expandableSectionIds.includes(sectionId)) {
        // Toggle the section (expand if collapsed, collapse if expanded)
        toggleSection(sectionId);

        // Dispatch custom event for SessionDetailedViewPage (used in addAddendum view)
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("session-detail-view-expand", {
              detail: { sectionId, behavior: "toggle" },
            })
          );
        }

        // If section was collapsed, expand it and scroll after a short delay to allow expansion
        if (!expandedSections.includes(sectionId)) {
          setTimeout(() => {
            const element = document.getElementById(sectionId);
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }, 100);
        } else {
          // If section was expanded, just scroll
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
        return;
      }
    }

    // For addAddendum view, also dispatch event for SessionDetailedViewPage
    if ((addAddendum || canEditAddendum()) && expandableSectionIds.includes(sectionId)) {
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("session-detail-view-expand", {
            detail: { sectionId, behavior: "toggle" },
          })
        );
      }

      // Scroll after a short delay to allow expansion
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
      return;
    }

    // For editable view or non-expandable sections, just scroll
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Handle symptom checkbox change
  const handleSymptomChange = (symptom) => {
    const currentSymptoms = sessionFormData.selectedSymptoms || [];
    if (currentSymptoms.includes(symptom)) {
      // Remove symptom
      const updatedSymptoms = currentSymptoms.filter((s) => s !== symptom);
      updateFormData("selectedSymptoms", updatedSymptoms);
      if (symptom === "Other") {
        updateFormData("otherSymptomText", "");
      }
    } else {
      // Add symptom
      updateFormData("selectedSymptoms", [...currentSymptoms, symptom]);
    }
    // Clear error when symptoms are selected
    if (symptomsError) {
      setSymptomsError("");
    }
  };

  // Diagnosis handlers - same as SessionHistoryDetailView
  const fetchPrimaryDiagnosisResults = useCallback(async (query = "") => {
    setDiagnosisSearchLoading(true);
    setDiagnosisSearchError("");
    try {
      const response = await axios.get("https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search", {
        params: {
          sf: "code,name",
          df: "code,name",
          terms: query || "",
          maxList: 25,
        },
      });
      const data = response.data;
      if (Array.isArray(data) && Array.isArray(data[3])) {
        const formatted = data[3].map((item) => ({
          code: item[0],
          description: item[1],
        }));
        setPrimaryDiagnosisResults(formatted);
      } else {
        setPrimaryDiagnosisResults([]);
      }
    } catch (error) {
      setDiagnosisSearchError("Unable to fetch diagnosis codes. Please try again.");
      setPrimaryDiagnosisResults([]);
    } finally {
      setDiagnosisSearchLoading(false);
    }
  }, []);

  const fetchSecondaryDiagnosisResults = useCallback(async (query = "") => {
    setSecondaryDiagnosisSearchLoading(true);
    setSecondaryDiagnosisSearchError("");
    try {
      const response = await axios.get("https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search", {
        params: {
          sf: "code,name",
          df: "code,name",
          terms: query || "",
          maxList: 25,
        },
      });
      const data = response.data;
      if (Array.isArray(data) && Array.isArray(data[3])) {
        const formatted = data[3].map((item) => ({
          code: item[0],
          description: item[1],
        }));
        setSecondaryDiagnosisResults(formatted);
      } else {
        setSecondaryDiagnosisResults([]);
      }
    } catch (error) {
      setSecondaryDiagnosisSearchError("Unable to fetch diagnosis codes. Please try again.");
      setSecondaryDiagnosisResults([]);
    } finally {
      setSecondaryDiagnosisSearchLoading(false);
    }
  }, []);

  const fetchTertiaryDiagnosisResults = useCallback(async (query = "") => {
    setTertiaryDiagnosisSearchLoading(true);
    setTertiaryDiagnosisSearchError("");
    try {
      const response = await axios.get("https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search", {
        params: {
          sf: "code,name",
          df: "code,name",
          terms: query || "",
          maxList: 25,
        },
      });
      const data = response.data;
      if (Array.isArray(data) && Array.isArray(data[3])) {
        const formatted = data[3].map((item) => ({
          code: item[0],
          description: item[1],
        }));
        setTertiaryDiagnosisResults(formatted);
      } else {
        setTertiaryDiagnosisResults([]);
      }
    } catch (error) {
      setTertiaryDiagnosisSearchError("Unable to fetch diagnosis codes. Please try again.");
      setTertiaryDiagnosisResults([]);
    } finally {
      setTertiaryDiagnosisSearchLoading(false);
    }
  }, []);

  const handlePrimaryDiagnosisSelect = (diagnosis) => {
    if (!diagnosis) return;
    updateFormData("primaryDiagnosis", diagnosis.code);
    updateFormData("primaryDiagnosisDescription", diagnosis.description);
    setPrimaryDiagnosisError("");
  };

  const togglePrimaryDiagnosisDropdown = () => {
    setIsPrimaryDiagnosisDropdownOpen((prev) => !prev);
  };

  const handleSecondaryDiagnosisSelect = (diagnosis) => {
    if (!diagnosis) return;
    updateFormData("secondaryDiagnosis", diagnosis.code);
    updateFormData("secondaryDiagnosisDescription", diagnosis.description);
  };

  const toggleSecondaryDiagnosisDropdown = () => {
    setIsSecondaryDiagnosisDropdownOpen((prev) => !prev);
  };

  const handleTertiaryDiagnosisSelect = (diagnosis) => {
    if (!diagnosis) return;
    updateFormData("tertiaryDiagnosis", diagnosis.code);
    updateFormData("tertiaryDiagnosisDescription", diagnosis.description);
  };

  const toggleTertiaryDiagnosisDropdown = () => {
    setIsTertiaryDiagnosisDropdownOpen((prev) => !prev);
  };

  // Diagnosis dropdown effects
  useEffect(() => {
    if (!isPrimaryDiagnosisDropdownOpen) return;
    setPrimaryDiagnosisSearchTerm("");
    fetchPrimaryDiagnosisResults("");
  }, [isPrimaryDiagnosisDropdownOpen, fetchPrimaryDiagnosisResults]);

  useEffect(() => {
    if (!isPrimaryDiagnosisDropdownOpen) return;
    const trimmedTerm = primaryDiagnosisSearchTerm.trim();
    const handler = setTimeout(() => {
      fetchPrimaryDiagnosisResults(trimmedTerm);
    }, trimmedTerm ? 500 : 0);
    return () => clearTimeout(handler);
  }, [primaryDiagnosisSearchTerm, isPrimaryDiagnosisDropdownOpen, fetchPrimaryDiagnosisResults]);

  useEffect(() => {
    if (!isPrimaryDiagnosisDropdownOpen) return;
    const handleClickOutside = (event) => {
      if (diagnosisCodeRef.current && !diagnosisCodeRef.current.contains(event.target)) {
        setIsPrimaryDiagnosisDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isPrimaryDiagnosisDropdownOpen]);

  useEffect(() => {
    if (!isSecondaryDiagnosisDropdownOpen) return;
    setSecondaryDiagnosisSearchTerm("");
    fetchSecondaryDiagnosisResults("");
  }, [isSecondaryDiagnosisDropdownOpen, fetchSecondaryDiagnosisResults]);

  useEffect(() => {
    if (!isSecondaryDiagnosisDropdownOpen) return;
    const trimmedTerm = secondaryDiagnosisSearchTerm.trim();
    const handler = setTimeout(() => {
      fetchSecondaryDiagnosisResults(trimmedTerm);
    }, trimmedTerm ? 500 : 0);
    return () => clearTimeout(handler);
  }, [secondaryDiagnosisSearchTerm, isSecondaryDiagnosisDropdownOpen, fetchSecondaryDiagnosisResults]);

  useEffect(() => {
    if (!isSecondaryDiagnosisDropdownOpen) return;
    const handleClickOutside = (event) => {
      if (secondaryDiagnosisCodeRef.current && !secondaryDiagnosisCodeRef.current.contains(event.target)) {
        setIsSecondaryDiagnosisDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSecondaryDiagnosisDropdownOpen]);

  useEffect(() => {
    if (!isTertiaryDiagnosisDropdownOpen) return;
    setTertiaryDiagnosisSearchTerm("");
    fetchTertiaryDiagnosisResults("");
  }, [isTertiaryDiagnosisDropdownOpen, fetchTertiaryDiagnosisResults]);

  useEffect(() => {
    if (!isTertiaryDiagnosisDropdownOpen) return;
    const trimmedTerm = tertiaryDiagnosisSearchTerm.trim();
    const handler = setTimeout(() => {
      fetchTertiaryDiagnosisResults(trimmedTerm);
    }, trimmedTerm ? 500 : 0);
    return () => clearTimeout(handler);
  }, [tertiaryDiagnosisSearchTerm, isTertiaryDiagnosisDropdownOpen, fetchTertiaryDiagnosisResults]);

  useEffect(() => {
    if (!isTertiaryDiagnosisDropdownOpen) return;
    const handleClickOutside = (event) => {
      if (tertiaryDiagnosisCodeRef.current && !tertiaryDiagnosisCodeRef.current.contains(event.target)) {
        setIsTertiaryDiagnosisDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isTertiaryDiagnosisDropdownOpen]);

  // Calculate client name, appointment date, and time (before early returns for useMemo)
  const clientName = session ? `${session.user?.firstname || ""} ${session.user?.lastname || ""}`.trim() : "";
  const appointmentDate = session ? (session.appointmentdate || session.appointmenttime) : null;

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };


  const appointmentTime = session?.appointmenttime ? formatDateTime(session.appointmenttime) : "N/A";
  const providerName = session ? `${session.health_specialist?.firstname || ""} ${session.health_specialist?.lastname || ""}`.trim() : "";

  // Create read-only sections array for expandable view (must be before early returns)
  const readOnlySections = useMemo(() => {
    if (!isSubmit || !sessionFormData || !session) return [];

    return [
      {
        id: "appointment-details",
        title: "Appointment Details",
        content: (
          <>
            <div className={styles.twoColumnLayout}>
              <div className={styles.column}>
                <div className={styles.fieldRow}>
                  <label className={styles.label}>Client</label>
                  <span className={styles.valueSpan} style={{ backgroundColor: "transparent" }}>
                    {clientName}
                  </span>
                </div>
                <div className={styles.fieldRow}>
                  <label className={styles.label}>Date</label>
                  <span className={styles.valueSpan} style={{ backgroundColor: "transparent" }}>
                    {appointmentDate ? formatDateOnly(appointmentDate) : "N/A"}
                  </span>
                </div>
                <div className={styles.fieldRow}>
                  <label className={styles.label}>Time In</label>
                  <span className={styles.valueSpanMinWidth} style={{ backgroundColor: "transparent", color: "black", fontWeight: "500" }}>
                    {formatTimeString(sessionFormData?.timeIn) || "N/A"}
                  </span>
                </div>
                <div className={styles.fieldRow}>
                  <label className={styles.label}>Duration</label>
                  <span className={styles.valueSpan} style={{ backgroundColor: "transparent" }}>
                    {durationDisplay}
                  </span>
                </div>
              </div>
              <div className={styles.column}>
                <div className={styles.fieldRow}>
                  <label className={styles.label}>Provider</label>
                  <span className={styles.valueSpan} style={{ backgroundColor: "transparent" }}>
                    {providerName}
                  </span>
                </div>
                <div className={styles.fieldRow}>
                  <label className={styles.label}>Scheduled Time</label>
                  <span className={styles.valueSpan} style={{ backgroundColor: "transparent" }}>
                    {appointmentTime}
                  </span>
                </div>
                <div className={styles.fieldRow}>
                  <label className={styles.label}>Time Out</label>
                  <span className={styles.valueSpanMinWidth} style={{ backgroundColor: "transparent", color: "black", fontWeight: "500" }}>
                    {formatTimeString(sessionFormData?.timeOut) || "N/A"}
                  </span>
                </div>
              </div>
            </div>
            <div className={styles.separator}></div>
            <div className={styles.fieldRowVertical} style={{ flexDirection: "row", paddingBottom: "20px", alignItems: "center" }}>
              <label className={styles.labelMinWidth} style={{ marginBottom: "0px" }}>
                Was the client at home?
              </label>
              <span className={styles.valueSpan1} style={{ backgroundColor: "transparent", color: "black", fontWeight: "400", textAlign: "left" }}>
                {sessionFormData?.wasClientAtHome ? capitalizeFirstChar(sessionFormData.wasClientAtHome) : "N/A"}
              </span>
            </div>
            {(sessionFormData?.primaryDiagnosis || sessionFormData?.secondaryDiagnosis || sessionFormData?.tertiaryDiagnosis) && (
              <>
                <div className={styles.separator} style={{ marginTop: "32px", marginBottom: "24px" }}></div>
                {sessionFormData?.primaryDiagnosis && (
                  <div style={{ marginBottom: "12px", textAlign: "left" }}>
                    <span style={{ fontSize: "16px", color: "#000", fontWeight: "normal" }}>
                      <span style={{ fontWeight: "500" }}>Primary Dx:</span> {sessionFormData.primaryDiagnosis} {sessionFormData.primaryDiagnosisDescription}
                    </span>
                  </div>
                )}
                {sessionFormData?.secondaryDiagnosis && (
                  <div style={{ marginBottom: "12px", textAlign: "left" }}>
                    <span style={{ fontSize: "16px", color: "#000", fontWeight: "normal" }}>
                      <span style={{ fontWeight: "500" }}>Secondary Dx:</span> {sessionFormData.secondaryDiagnosis} {sessionFormData.secondaryDiagnosisDescription}
                    </span>
                  </div>
                )}
                {sessionFormData?.tertiaryDiagnosis && (
                  <div style={{ marginBottom: "12px", textAlign: "left" }}>
                    <span style={{ fontSize: "16px", color: "#000", fontWeight: "normal" }}>
                      <span style={{ fontWeight: "500" }}>Tertiary Dx:</span> {sessionFormData.tertiaryDiagnosis} {sessionFormData.tertiaryDiagnosisDescription}
                    </span>
                  </div>
                )}
              </>
            )}
          </>
        )
      },
      {
        id: "client-presentation",
        title: "Client Presentation",
        content: (
          <>
            <div style={{ marginBottom: "24px" }}>
              <label className={styles.labelWithMargin}>Focus of the session</label>
              <div style={{ padding: "0", backgroundColor: "transparent", minHeight: "auto", fontSize: "16px", color: "#000", whiteSpace: "pre-wrap", wordWrap: "break-word", textAlign: "left", fontWeight: "normal" }}>
                {sessionFormData?.focusOfSession || "No focus of session entered"}
              </div>
            </div>
            <div style={{ marginBottom: "24px" }}>
              <label className={styles.labelWithMargin}>Symptoms</label>
              <div style={{ padding: "0", backgroundColor: "transparent", fontSize: "16px", color: "#000", textAlign: "left", fontWeight: "normal" }}>
                {sessionFormData?.selectedSymptoms && sessionFormData.selectedSymptoms.length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: "20px" }}>
                    {sessionFormData.selectedSymptoms.map((symptom, index) => (
                      <li key={index}>
                        {symptom === "Other" && sessionFormData?.otherSymptomText
                          ? sessionFormData.otherSymptomText
                          : symptom}
                      </li>
                    ))}
                  </ul>
                ) : "No symptoms selected"}
              </div>
            </div>
            <div style={{ marginBottom: "16px", textAlign: "left" }}>
              <span style={{ fontSize: "16px", color: "#000", fontWeight: "normal" }}>
                <span style={{ fontWeight: "500" }}>Mood / Affect:</span> {sessionFormData?.moodAffect === "Other" && sessionFormData?.otherMoodAffectText
                  ? sessionFormData.otherMoodAffectText
                  : (sessionFormData?.moodAffect || "N/A")}
              </span>
            </div>
            <div style={{ marginBottom: "16px", textAlign: "left" }}>
              <span style={{ fontSize: "16px", color: "#000", fontWeight: "normal" }}>
                <span style={{ fontWeight: "500" }}>Behavior / Engagement:</span> {sessionFormData?.behaviorEngagement === "Other" && sessionFormData?.otherBehaviorEngagementText
                  ? sessionFormData.otherBehaviorEngagementText
                  : (sessionFormData?.behaviorEngagement || "N/A")}
              </span>
            </div>
          </>
        )
      },
      {
        id: "interventions",
        title: "Interventions",
        content: (
          <>
            <div style={{ marginBottom: "24px" }}>
              <label className={styles.labelWithMargin}>Describe the interventions used</label>
              <div style={{ padding: "0", backgroundColor: "transparent", minHeight: "auto", fontSize: "16px", color: "#000", whiteSpace: "pre-wrap", wordWrap: "break-word", textAlign: "left", fontWeight: "normal" }}>
                {sessionFormData?.interventionsUsed || "No interventions entered"}
              </div>
            </div>
          </>
        )
      },
      {
        id: "client-progress",
        title: "Client Progress",
        content: (
          <>
            <div style={{ marginBottom: "24px" }}>
              <label className={styles.labelWithMargin}>Client response to care</label>
              <div style={{ padding: "0", backgroundColor: "transparent", minHeight: "auto", fontSize: "16px", color: "#000", whiteSpace: "pre-wrap", wordWrap: "break-word", textAlign: "left", fontWeight: "normal" }}>
                {sessionFormData?.clientResponseToCare || "No response entered"}
              </div>
            </div>
            <div style={{ marginBottom: "24px" }}>
              <label className={styles.labelWithMargin}>Progress toward goals</label>
              <div style={{ padding: "0", backgroundColor: "transparent", fontSize: "16px", color: "#000", textAlign: "left", fontWeight: "normal" }}>
                {sessionFormData?.progressTowardGoals && sessionFormData.progressTowardGoals.length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: "20px" }}>
                    {sessionFormData.progressTowardGoals.map((goal, index) => (
                      <li key={index}>
                        {goal}
                        {goal === "Goal Met" && sessionFormData?.goalMetDescription && (
                          <span style={{ marginLeft: "8px", color: "#666" }}>
                            ({sessionFormData.goalMetDescription})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : "No progress goals selected"}
              </div>
            </div>
            <div style={{ marginBottom: "24px" }}>
              <label className={styles.labelWithMargin}>Barriers to Progress</label>
              <div style={{ padding: "0", backgroundColor: "transparent", fontSize: "16px", color: "#000", textAlign: "left", fontWeight: "normal" }}>
                {sessionFormData?.barriersToProgress && sessionFormData.barriersToProgress.length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: "20px" }}>
                    {sessionFormData.barriersToProgress.map((barrier, index) => (
                      <li key={index}>
                        {barrier === "Other" && sessionFormData?.otherBarrierText
                          ? sessionFormData.otherBarrierText
                          : barrier}
                      </li>
                    ))}
                  </ul>
                ) : "No barriers selected"}
              </div>
            </div>
            <div style={{ marginBottom: "24px" }}>
              <label className={styles.labelWithMargin}>Additional Notes</label>
              <div style={{ padding: "0", backgroundColor: "transparent", minHeight: "auto", fontSize: "16px", color: "#000", whiteSpace: "pre-wrap", wordWrap: "break-word", textAlign: "left", fontWeight: "normal" }}>
                {sessionFormData?.additionalNotes || "No additional notes entered"}
              </div>
            </div>
          </>
        )
      },
      {
        id: "care-plan",
        title: "Care Plan",
        content: (
          <>
            <div style={{ marginBottom: "24px" }}>
              <label className={styles.labelWithMargin}>Client Goal</label>
              <div style={{ padding: "0", backgroundColor: "transparent", minHeight: "auto", fontSize: "16px", color: "#000", whiteSpace: "pre-wrap", wordWrap: "break-word", textAlign: "left", fontWeight: "normal" }}>
                {sessionFormData?.clientGoal || "No client goal entered"}
              </div>
            </div>
            <div style={{ marginBottom: "24px" }}>
              <label className={styles.labelWithMargin}>Objectives</label>
              <div style={{ padding: "0", backgroundColor: "transparent", minHeight: "auto", fontSize: "16px", color: "#000", whiteSpace: "pre-wrap", wordWrap: "break-word", textAlign: "left", fontWeight: "normal" }}>
                {sessionFormData?.objectives || "No objectives entered"}
              </div>
            </div>
            <div style={{ marginBottom: "24px" }}>
              <label className={styles.labelWithMargin}>Interventions</label>
              <div style={{ padding: "0", backgroundColor: "transparent", minHeight: "auto", fontSize: "16px", color: "#000", whiteSpace: "pre-wrap", wordWrap: "break-word", textAlign: "left", fontWeight: "normal" }}>
                {sessionFormData?.interventions || "No interventions entered"}
              </div>
            </div>
            <div style={{ marginBottom: "16px", display: "flex", flexDirection: "row", gap: "12px", alignItems: "center" }}>
              <label className={styles.labelWithMarginXLarge} style={{ marginBottom: "0px" }}>
                Session Frequency:
              </label>
              <span className={styles.valueSpan1} style={{ backgroundColor: "transparent", color: "black", fontWeight: "400", textAlign: "left", padding: "0px" }}>
                {sessionFormData?.sessionFrequency === "Other" && sessionFormData?.otherSessionFrequencyText
                  ? sessionFormData.otherSessionFrequencyText
                  : (sessionFormData?.sessionFrequency || "No frequency selected")}
              </span>
            </div>
            <div style={{ marginBottom: "16px", display: "flex", flexDirection: "row", gap: "12px", alignItems: "center" }}>
              <label className={styles.labelWithMarginXLarge} style={{ marginBottom: "0px" }}>
                Review Date:
              </label>
              <span className={styles.valueSpan1} style={{ backgroundColor: "transparent", color: "black", fontWeight: "400", textAlign: "left", padding: "0px" }}>
                {sessionFormData?.reviewDate ? formatDate(sessionFormData.reviewDate) : "No review date selected"}
              </span>
            </div>
          </>
        )
      }
    ];
  }, [isSubmit, sessionFormData, clientName, providerName, appointmentDate, appointmentTime, durationDisplay, formatDate, formatTimeString, capitalizeFirstChar, session]);

  // Expand all read-only sections by default when viewing follow-up in read-only mode
  // Placed after readOnlySections is defined to avoid ReferenceError
  useEffect(() => {
    if (isSubmit && readOnlySections?.length > 0) {
      const allSectionIds = readOnlySections.map(({ id }) => id);
      setExpandedSections((prev) => {
        const allExpanded = allSectionIds.every(id => prev.includes(id));
        return allExpanded ? prev : allSectionIds;
      });
    }
  }, [isSubmit, session?._id]);

  // Early return after all hooks
  if (!session) {
    return (
      <div className="container mt-5">
        <Card>
          <Card.Body className="text-center py-5">
            <h4>No Session Data Available</h4>
            <Button onClick={handleBackNavigation} variant="primary" className="mt-3">
              Back to Session History
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (loading || !dataLoaded) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <p>Loading follow-up data...</p>
        </div>
      </div>
    );
  }

  // Get provider credentials
  const providerCredentials = formatCredentials(session?.health_specialist?.credentials) || "LPC";

  return (
    <div className={styles.container}>
      <ToastContainer position="top-right" />
      {/* Header */}
      <div className={styles.headerContainer}>
        {/* Follow-Up Card */}
        <Card className={styles.headerCard}>
          <span className={styles.headerTitle}>
            {(() => {
              // If addAddendum is true, show addendum number
              if (addAddendum) {
                return getAddendumNumber();
              }

              // If addAddendum is false, check latest addendumData entry
              if (addendumData && addendumData.length > 0) {
                const latestAddendum = addendumData[addendumData.length - 1];
                // If latest entry's addendumSigned is false and addendumSubmit is true, show addendum number
                if (latestAddendum && latestAddendum.addendumSigned === false && latestAddendum.addendumSubmit === true) {
                  return getAddendumNumber();
                }
              }

              // Otherwise show Follow-Up
              return "Follow-Up";
            })()}
          </span>

          <span className={styles.headerSubtitle}>
            {clientName} · {appointmentDate ? formatDateOnly(appointmentDate) : "N/A"}, {appointmentTime}
          </span>
        </Card>

        <SessionActionButtons
          onBack={handleBackNavigation}
          saving={saving}
          signingAndSubmitting={signingAndSubmitting}
          saveStatus={saveStatus}
          onSave={handleSave}
          onEdit={handleEdit}
          onDownload={handleDownload}
          onAddAddendum={handleAddAddendum}
          onEditAddendum={handleEditAddendum}
          onAddendumSubmit={handleAddendumSubmit}
          isSigned={isSigned}
          addAddendum={addAddendum}
          isSubmit={isSubmit}
          canEditAddendum={canEditAddendum()}
          showSubmitAndPreview={true}
          onSubmitAndPreview={handleSignAndSubmit}
          editExistingAddendum={editExistingAddendum}
        />
      </div>

      <div className={styles.contentContainer} style={{ alignItems: "flex-start" }}>
        <Card className={styles.sidebarCard} style={{ alignSelf: "flex-start" }}>
          <div className={styles.sidebarContainer}>
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={styles.sidebarButton}
                style={{
                  backgroundColor: activeSection === section.id ? "#e7f3ff" : "transparent",
                  fontWeight: activeSection === section.id ? "600" : "400",
                }}
                onMouseEnter={(e) => {
                  if (activeSection !== section.id) {
                    e.target.style.backgroundColor = "#f8f8f8";
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSection !== section.id) {
                    e.target.style.backgroundColor = "transparent";
                  }
                }}>
                {section.label}
              </button>
            ))}
          </div>
        </Card>

        {/* Content Area */}
        <div className={styles.contentWrapper} style={{ alignSelf: "flex-start" }}>
          {isSubmit && (addAddendum || canEditAddendum()) ? (
            <div id="addendum">
              <AddendumView
                addendumText={sessionFormData.addendumText}
                onAddendumTextChange={(value) => {
                  updateFormData("addendumText", value);
                  // Clear error when user starts typing
                  if (addendumTextError) {
                    setAddendumTextError("");
                  }
                }}
                autoResizeTextarea={autoResizeTextarea}
                addendumNumber={getAddendumNumber()}
                addendumTextError={addendumTextError}
                canEditAddendum={canEditAddendum()}
                addendumData={addendumData}
                session={session}
                sessionFormData={sessionFormData}
                // Pass Follow-Up specific props for expandable sections
                readOnlySections={readOnlySections}
                expandedSections={expandedSections}
                toggleSection={toggleSection}
                formatTimeString={formatTimeString}
                capitalizeFirstChar={capitalizeFirstChar}
                durationDisplay={durationDisplay}
                clientName={clientName}
                providerName={providerName}
                appointmentDate={appointmentDate}
                appointmentTime={appointmentTime}
                formatDate={formatDate}
              />
            </div>
          ) : isSubmit && isSigned ? (
            // Read-only view when signed - show expandable sections
            <>
              <Card className={styles.card}>
                <div className={styles.collapsibleStack}>
                  {readOnlySections.map(({ id, title, content }) => {
                    const isExpanded = expandedSections.includes(id);
                    return (
                      <div id={id} key={id} className={styles.collapsibleSection}>
                        <button
                          type="button"
                          className={styles.collapsibleHeader}
                          onClick={() => toggleSection(id)}
                          aria-expanded={isExpanded}
                          aria-controls={`${id}-content`}
                        >
                          <span>{title}</span>
                        </button>
                        <div
                          id={`${id}-content`}
                          className={isExpanded ? styles.collapsibleBodyExpanded : styles.collapsibleBodyCollapsed}
                        >
                          {content}
                          {isExpanded && <div className={styles.collapsibleSeparator} />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Old read-only view - keeping for reference but commented out */}
              {false && (
                <Card id="appointment-details" className={styles.card}>
                  <h3 className={styles.sectionHeading}>Appointment Details</h3>
                  <div className={styles.twoColumnLayout}>
                    <div className={styles.column}>
                      <div className={styles.fieldRow}>
                        <label className={styles.label}>Client</label>
                        <span className={styles.valueSpan} style={{ backgroundColor: "transparent", border: "none", padding: "0", fontWeight: "normal" }}>
                          {clientName}
                        </span>
                      </div>
                      <div className={styles.fieldRow}>
                        <label className={styles.label}>Date</label>
                        <span className={styles.valueSpan} style={{ backgroundColor: "transparent", border: "none", padding: "0", fontWeight: "normal" }}>
                          {appointmentDate ? formatDateOnly(appointmentDate) : "N/A"}
                        </span>
                      </div>
                      <div className={styles.fieldRow}>
                        <label className={styles.label}>Time In</label>
                        <span className={styles.valueSpan} style={{ backgroundColor: "transparent", border: "none", padding: "0", fontWeight: "normal" }}>
                          {sessionFormData?.timeIn || "N/A"}
                        </span>
                      </div>
                      <div className={styles.fieldRow}>
                        <label className={styles.label}>Duration</label>
                        <span className={styles.valueSpan} style={{ backgroundColor: "transparent", border: "none", padding: "0", fontWeight: "normal" }}>
                          {durationDisplay}
                        </span>
                      </div>
                    </div>
                    <div className={styles.column}>
                      <div className={styles.fieldRow}>
                        <label className={styles.label}>Provider</label>
                        <span className={styles.valueSpan} style={{ backgroundColor: "transparent", border: "none", padding: "0", fontWeight: "normal" }}>
                          {providerName}
                        </span>
                      </div>
                      <div className={styles.fieldRow}>
                        <label className={styles.label}>Scheduled Time</label>
                        <span className={styles.valueSpan} style={{ backgroundColor: "transparent", border: "none", padding: "0", fontWeight: "normal" }}>
                          {appointmentTime}
                        </span>
                      </div>
                      <div className={styles.fieldRow}>
                        <label className={styles.label}>Time Out</label>
                        <span className={styles.valueSpan} style={{ backgroundColor: "transparent", border: "none", padding: "0", fontWeight: "normal" }}>
                          {sessionFormData?.timeOut || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.separator}></div>
                  <div className={styles.fieldRow}>
                    <label className={styles.label}>Was the client at home?</label>
                    <span className={styles.valueSpan} style={{ backgroundColor: "transparent", border: "none", padding: "0", fontWeight: "normal" }}>
                      {sessionFormData?.wasClientAtHome === "yes" ? "Yes" : sessionFormData?.wasClientAtHome === "no" ? "No" : "N/A"}
                    </span>
                  </div>
                  {(
                    <>
                      <div className={styles.separator} style={{ marginTop: "32px", marginBottom: "24px" }}></div>
                      {sessionFormData?.primaryDiagnosis && (
                        <div style={{ marginBottom: "12px", textAlign: "left" }}>
                          <span style={{ fontSize: "16px", color: "#000", fontWeight: "normal" }}>
                            <span style={{ fontWeight: "500" }}>Primary Dx:</span> {sessionFormData.primaryDiagnosis} {sessionFormData.primaryDiagnosisDescription}
                          </span>
                        </div>
                      )}
                      {sessionFormData?.secondaryDiagnosis && (
                        <div style={{ marginBottom: "12px", textAlign: "left" }}>
                          <span style={{ fontSize: "16px", color: "#000", fontWeight: "normal" }}>
                            <span style={{ fontWeight: "500" }}>Secondary Dx:</span> {sessionFormData.secondaryDiagnosis} {sessionFormData.secondaryDiagnosisDescription}
                          </span>
                        </div>
                      )}
                      {sessionFormData?.tertiaryDiagnosis && (
                        <div style={{ marginBottom: "12px", textAlign: "left" }}>
                          <span style={{ fontSize: "16px", color: "#000", fontWeight: "normal" }}>
                            <span style={{ fontWeight: "500" }}>Tertiary Dx:</span> {sessionFormData.tertiaryDiagnosis} {sessionFormData.tertiaryDiagnosisDescription}
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  <div className={styles.separator} style={{ marginTop: "32px", marginBottom: "24px" }}></div>
                  <h3 className={styles.sectionHeading} id="client-presentation">Client Presentation</h3>
                  <div style={{ marginBottom: "24px" }}>
                    <label className={styles.labelWithMargin}>Focus of the session</label>
                    <div style={{ padding: "0", backgroundColor: "transparent", minHeight: "auto", fontSize: "16px", color: "#000", whiteSpace: "pre-wrap", wordWrap: "break-word", textAlign: "left", fontWeight: "normal" }}>
                      {sessionFormData?.focusOfSession || "No focus of session entered"}
                    </div>
                  </div>
                  <div style={{ marginBottom: "24px" }}>
                    <label className={styles.labelWithMargin}>Symptoms</label>
                    <div style={{ padding: "0", backgroundColor: "transparent", fontSize: "16px", color: "#000", textAlign: "left", fontWeight: "normal" }}>
                      {sessionFormData?.selectedSymptoms && sessionFormData.selectedSymptoms.length > 0 ? (
                        <ul style={{ margin: 0, paddingLeft: "20px" }}>
                          {sessionFormData.selectedSymptoms.map((symptom, index) => (
                            <li key={index}>
                              {symptom === "Other" && sessionFormData?.otherSymptomText
                                ? sessionFormData.otherSymptomText
                                : symptom}
                            </li>
                          ))}
                        </ul>
                      ) : "No symptoms selected"}
                    </div>
                  </div>
                  <div style={{ marginBottom: "16px", textAlign: "left" }}>
                    <span style={{ fontSize: "16px", color: "#000", fontWeight: "normal" }}>
                      <span style={{ fontWeight: "500" }}>Mood / Affect:</span> {sessionFormData?.moodAffect === "Other" && sessionFormData?.otherMoodAffectText
                        ? sessionFormData.otherMoodAffectText
                        : (sessionFormData?.moodAffect || "N/A")}
                    </span>
                  </div>
                  <div style={{ marginBottom: "16px", textAlign: "left" }}>
                    <span style={{ fontSize: "16px", color: "#000", fontWeight: "normal" }}>
                      <span style={{ fontWeight: "500" }}>Behavior / Engagement:</span> {sessionFormData?.behaviorEngagement === "Other" && sessionFormData?.otherBehaviorEngagementText
                        ? sessionFormData.otherBehaviorEngagementText
                        : (sessionFormData?.behaviorEngagement || "N/A")}
                    </span>
                  </div>

                  <div className={styles.separator} style={{ marginTop: "32px", marginBottom: "24px" }}></div>
                  <h3 className={styles.sectionHeading} id="interventions">Interventions</h3>
                  <div style={{ marginBottom: "24px" }}>
                    <label className={styles.labelWithMargin}>Describe the interventions used</label>
                    <div style={{ padding: "0", backgroundColor: "transparent", minHeight: "auto", fontSize: "16px", color: "#000", whiteSpace: "pre-wrap", wordWrap: "break-word", textAlign: "left", fontWeight: "normal" }}>
                      {sessionFormData?.interventionsUsed || "No interventions entered"}
                    </div>
                  </div>

                  <div className={styles.separator} style={{ marginTop: "32px", marginBottom: "24px" }}></div>
                  <h3 className={styles.sectionHeading} id="client-progress">Client Progress</h3>
                  <div style={{ marginBottom: "24px" }}>
                    <label className={styles.labelWithMargin}>Client response to care</label>
                    <div style={{ padding: "0", backgroundColor: "transparent", minHeight: "auto", fontSize: "16px", color: "#000", whiteSpace: "pre-wrap", wordWrap: "break-word", textAlign: "left", fontWeight: "normal" }}>
                      {sessionFormData?.clientResponseToCare || "No response entered"}
                    </div>
                  </div>
                  <div style={{ marginBottom: "24px" }}>
                    <label className={styles.labelWithMargin}>Progress toward goals</label>
                    <div style={{ padding: "0", backgroundColor: "transparent", fontSize: "16px", color: "#000", textAlign: "left", fontWeight: "normal" }}>
                      {sessionFormData?.progressTowardGoals && sessionFormData.progressTowardGoals.length > 0 ? (
                        <ul style={{ margin: 0, paddingLeft: "20px" }}>
                          {sessionFormData.progressTowardGoals.map((goal, index) => (
                            <li key={index}>
                              {goal}
                              {goal === "Goal Met" && sessionFormData?.goalMetDescription && (
                                <span style={{ marginLeft: "8px", color: "#666" }}>
                                  ({sessionFormData.goalMetDescription})
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : "No progress goals selected"}
                    </div>
                  </div>
                  <div style={{ marginBottom: "24px" }}>
                    <label className={styles.labelWithMargin}>Barriers to Progress</label>
                    <div style={{ padding: "0", backgroundColor: "transparent", fontSize: "16px", color: "#000", textAlign: "left", fontWeight: "normal" }}>
                      {sessionFormData?.barriersToProgress && sessionFormData.barriersToProgress.length > 0 ? (
                        <ul style={{ margin: 0, paddingLeft: "20px" }}>
                          {sessionFormData.barriersToProgress.map((barrier, index) => (
                            <li key={index}>
                              {barrier === "Other" && sessionFormData?.otherBarrierText
                                ? sessionFormData.otherBarrierText
                                : barrier}
                            </li>
                          ))}
                        </ul>
                      ) : "No barriers selected"}
                    </div>
                  </div>
                  <div style={{ marginBottom: "24px" }}>
                    <label className={styles.labelWithMargin}>Additional Notes</label>
                    <div style={{ padding: "0", backgroundColor: "transparent", minHeight: "auto", fontSize: "16px", color: "#000", whiteSpace: "pre-wrap", wordWrap: "break-word", textAlign: "left", fontWeight: "normal" }}>
                      {sessionFormData?.additionalNotes || "No additional notes entered"}
                    </div>
                  </div>

                  <div className={styles.separator} style={{ marginTop: "32px", marginBottom: "24px" }}></div>
                  <h3 className={styles.sectionHeading} id="care-plan">Care Plan</h3>
                  <div style={{ marginBottom: "24px" }}>
                    <label className={styles.labelWithMargin}>Client Goal</label>
                    <div style={{ padding: "0", backgroundColor: "transparent", minHeight: "auto", fontSize: "16px", color: "#000", whiteSpace: "pre-wrap", wordWrap: "break-word", textAlign: "left", fontWeight: "normal" }}>
                      {sessionFormData?.clientGoal || "No client goal entered"}
                    </div>
                  </div>
                  <div style={{ marginBottom: "24px" }}>
                    <label className={styles.labelWithMargin}>Objectives</label>
                    <div style={{ padding: "0", backgroundColor: "transparent", minHeight: "auto", fontSize: "16px", color: "#000", whiteSpace: "pre-wrap", wordWrap: "break-word", textAlign: "left", fontWeight: "normal" }}>
                      {sessionFormData?.objectives || "No objectives entered"}
                    </div>
                  </div>
                  <div style={{ marginBottom: "24px" }}>
                    <label className={styles.labelWithMargin}>Interventions</label>
                    <div style={{ padding: "0", backgroundColor: "transparent", minHeight: "auto", fontSize: "16px", color: "#000", whiteSpace: "pre-wrap", wordWrap: "break-word", textAlign: "left", fontWeight: "normal" }}>
                      {sessionFormData?.interventions || "No interventions entered"}
                    </div>
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <label className={styles.labelWithMarginXLarge} style={{ marginBottom: "0", whiteSpace: "nowrap" }}>
                        Frequency:
                      </label>
                      <div style={{ padding: "0", backgroundColor: "transparent", fontSize: "16px", color: "#000", textAlign: "left", fontWeight: "normal" }}>
                        {sessionFormData?.sessionFrequency === "Other" && sessionFormData?.otherSessionFrequencyText
                          ? sessionFormData.otherSessionFrequencyText
                          : (sessionFormData?.sessionFrequency || "No frequency selected")}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <label className={styles.labelWithMarginXLarge} style={{ marginBottom: "0", whiteSpace: "nowrap" }}>
                        Review Date:
                      </label>
                      <div style={{ padding: "0", backgroundColor: "transparent", fontSize: "16px", color: "#000", textAlign: "left", fontWeight: "normal" }}>
                        {sessionFormData?.reviewDate ? formatDate(sessionFormData.reviewDate) : "No review date selected"}
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Addendums - Show signed addendums */}
              {addendumData && Array.isArray(addendumData) && addendumData.length > 0 && (
                <div id="addendum">
                  {addendumData.map((addendum, index) => {
                    if (addendum.addendumSigned === true && addendum.addendumText) {
                      const addendumNumber = index + 1;
                      return (
                        <Card key={`addendum-${index}`} id={`addendum-${index}`} className={styles.card}>
                          <h3 className={styles.sectionHeading}>Addendum # {addendumNumber}</h3>
                          <div style={{ padding: "0", backgroundColor: "transparent", minHeight: "auto", fontSize: "16px", color: "#000", whiteSpace: "pre-wrap", wordWrap: "break-word", textAlign: "left" }}>
                            {addendum.addendumText}
                          </div>
                        </Card>
                      );
                    }
                    return null;
                  })}
                </div>
              )}
            </>
          ) : isSubmit ? (
            // Read-only view when submitted - show expandable sections
            <>
              <Card className={styles.card}>
                <div className={styles.collapsibleStack}>
                  {readOnlySections.map(({ id, title, content }) => {
                    const isExpanded = expandedSections.includes(id);
                    return (
                      <div id={id} key={id} className={styles.collapsibleSection}>
                        <button
                          type="button"
                          className={styles.collapsibleHeader}
                          onClick={() => toggleSection(id)}
                          aria-expanded={isExpanded}
                          aria-controls={`${id}-content`}
                        >
                          <span>{title}</span>
                        </button>
                        <div
                          id={`${id}-content`}
                          className={isExpanded ? styles.collapsibleBodyExpanded : styles.collapsibleBodyCollapsed}
                        >
                          {content}
                          {isExpanded && <div className={styles.collapsibleSeparator} />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Addendums - Show signed addendums */}
              {addendumData && Array.isArray(addendumData) && addendumData.length > 0 && (
                <div id="addendum">
                  {addendumData.map((addendum, index) => {
                    if (addendum.addendumSigned === true && addendum.addendumText) {
                      const addendumNumber = index + 1;
                      return (
                        <Card key={`addendum-${index}`} id={`addendum-${index}`} className={styles.card}>
                          <h3 className={styles.sectionHeading}>Addendum # {addendumNumber}</h3>
                          <div style={{ padding: "0", backgroundColor: "transparent", minHeight: "auto", fontSize: "16px", color: "#000", whiteSpace: "pre-wrap", wordWrap: "break-word", textAlign: "left" }}>
                            {addendum.addendumText}
                          </div>
                        </Card>
                      );
                    }
                    return null;
                  })}
                </div>
              )}
            </>
          ) : (
            // Editable view
            <>
              <AppointmentDetailsSection
                clientName={clientName}
                providerName={providerName}
                appointmentDate={appointmentDate}
                appointmentTime={appointmentTime}
                durationDisplay={durationDisplay}
                sessionFormData={sessionFormData}
                updateFormData={updateFormData}
                timeInParts={timeInParts}
                timeOutParts={timeOutParts}
                isTimeInPickerOpen={isTimeInPickerOpen}
                isTimeOutPickerOpen={isTimeOutPickerOpen}
                timeInPickerRef={timeInPickerRef}
                timeOutPickerRef={timeOutPickerRef}
                handleTimeInputFocus={handleTimeInputFocus}
                setIsTimeInPickerOpen={setIsTimeInPickerOpen}
                setIsTimeOutPickerOpen={setIsTimeOutPickerOpen}
                handleTimeSegmentChange={handleTimeSegmentChange}
                setTimeInParts={setTimeInParts}
                setTimeOutParts={setTimeOutParts}
                handleTimeSegmentBlur={handleTimeSegmentBlur}
                handleHourAdjust={handleHourAdjust}
                handleMinuteAdjust={handleMinuteAdjust}
                handlePeriodToggle={handlePeriodToggle}
                formatDate={formatDate}
                isFollowUp={true}
                // Diagnosis props
                primaryDiagnosisError={primaryDiagnosisError}
                diagnosisCodeRef={diagnosisCodeRef}
                secondaryDiagnosisCodeRef={secondaryDiagnosisCodeRef}
                tertiaryDiagnosisCodeRef={tertiaryDiagnosisCodeRef}
                isPrimaryDiagnosisDropdownOpen={isPrimaryDiagnosisDropdownOpen}
                isSecondaryDiagnosisDropdownOpen={isSecondaryDiagnosisDropdownOpen}
                isTertiaryDiagnosisDropdownOpen={isTertiaryDiagnosisDropdownOpen}
                togglePrimaryDiagnosisDropdown={togglePrimaryDiagnosisDropdown}
                toggleSecondaryDiagnosisDropdown={toggleSecondaryDiagnosisDropdown}
                toggleTertiaryDiagnosisDropdown={toggleTertiaryDiagnosisDropdown}
                primaryDiagnosisSearchTerm={primaryDiagnosisSearchTerm}
                secondaryDiagnosisSearchTerm={secondaryDiagnosisSearchTerm}
                tertiaryDiagnosisSearchTerm={tertiaryDiagnosisSearchTerm}
                setPrimaryDiagnosisSearchTerm={setPrimaryDiagnosisSearchTerm}
                setSecondaryDiagnosisSearchTerm={setSecondaryDiagnosisSearchTerm}
                setTertiaryDiagnosisSearchTerm={setTertiaryDiagnosisSearchTerm}
                diagnosisSearchLoading={diagnosisSearchLoading}
                secondaryDiagnosisSearchLoading={secondaryDiagnosisSearchLoading}
                tertiaryDiagnosisSearchLoading={tertiaryDiagnosisSearchLoading}
                diagnosisSearchError={diagnosisSearchError}
                secondaryDiagnosisSearchError={secondaryDiagnosisSearchError}
                tertiaryDiagnosisSearchError={tertiaryDiagnosisSearchError}
                primaryDiagnosisResults={primaryDiagnosisResults}
                secondaryDiagnosisResults={secondaryDiagnosisResults}
                tertiaryDiagnosisResults={tertiaryDiagnosisResults}
                handlePrimaryDiagnosisSelect={handlePrimaryDiagnosisSelect}
                handleSecondaryDiagnosisSelect={handleSecondaryDiagnosisSelect}
                handleTertiaryDiagnosisSelect={handleTertiaryDiagnosisSelect}
                setIsPrimaryDiagnosisDropdownOpen={setIsPrimaryDiagnosisDropdownOpen}
                setIsSecondaryDiagnosisDropdownOpen={setIsSecondaryDiagnosisDropdownOpen}
                setIsTertiaryDiagnosisDropdownOpen={setIsTertiaryDiagnosisDropdownOpen}
              />

              <ClientPresentationSection
                sessionFormData={sessionFormData}
                updateFormData={updateFormData}
                focusOfSessionError={focusOfSessionError}
                symptomsError={symptomsError}
                moodAffectError={moodAffectError}
                behaviorEngagementError={behaviorEngagementError}
                handleSymptomChange={handleSymptomChange}
                autoResizeTextarea={autoResizeTextarea}
                setFocusOfSessionError={setFocusOfSessionError}
                setMoodAffectError={setMoodAffectError}
                setBehaviorEngagementError={setBehaviorEngagementError}
              />

              <InterventionsSection
                sessionFormData={sessionFormData}
                updateFormData={updateFormData}
                interventionsError={interventionsUsedError}
                setInterventionsError={setInterventionsUsedError}
                autoResizeTextarea={autoResizeTextarea}
              />

              <ClientProgressSection
                sessionFormData={sessionFormData}
                updateFormData={updateFormData}
                clientResponseToCareError={clientResponseToCareError}
                progressTowardGoalsError={progressTowardGoalsError}
                barriersToProgressError={barriersToProgressError}
                setClientResponseToCareError={setClientResponseToCareError}
                setProgressTowardGoalsError={setProgressTowardGoalsError}
                setBarriersToProgressError={setBarriersToProgressError}
                autoResizeTextarea={autoResizeTextarea}
              />

              <CarePlanConcernsSection
                sessionFormData={sessionFormData}
                updateFormData={updateFormData}
                clientGoalError={clientGoalError}
                objectivesError={objectivesError}
                interventionsError={interventionsError}
                sessionFrequencyError={sessionFrequencyError}
                reviewDateError={reviewDateError}
                setClientGoalError={setClientGoalError}
                setObjectivesError={setObjectivesError}
                setInterventionsError={setInterventionsError}
                setSessionFrequencyError={setSessionFrequencyError}
                setReviewDateError={setReviewDateError}
                autoResizeTextarea={autoResizeTextarea}
                isFollowUp={true}
                carePlanEditMode={carePlanEditMode}
                onUpdateClick={() => {
                  setCarePlanEditMode("update");
                }}
                onCreateNewClick={() => {
                  // Store current data before clearing (for cancel functionality)
                  const currentCarePlanData = {
                    clientGoal: sessionFormData.clientGoal || "",
                    objectives: sessionFormData.objectives || "",
                    interventions: sessionFormData.interventions || "",
                    sessionFrequency: sessionFormData.sessionFrequency || "",
                    otherSessionFrequencyText: sessionFormData.otherSessionFrequencyText || "",
                    reviewDate: sessionFormData.reviewDate || "",
                  };
                  // Only update originalCarePlanData if it's not already set
                  if (!originalCarePlanData) {
                    setOriginalCarePlanData(currentCarePlanData);
                  }
                  setCarePlanEditMode("create");
                  // Calculate review date as +180 days from now
                  const today = new Date();
                  const reviewDate = new Date(today);
                  reviewDate.setDate(today.getDate() + 180);
                  const formattedReviewDate = reviewDate.toISOString().split('T')[0];
                  // Clear care plan fields for new entry and set review date to +180 days
                  setSessionFormData(prev => ({
                    ...prev,
                    clientGoal: "",
                    objectives: "",
                    interventions: "",
                    sessionFrequency: "",
                    otherSessionFrequencyText: "",
                    reviewDate: formattedReviewDate,
                  }));
                }}
                onCancelEdit={() => {
                  if (carePlanEditMode === "update" && originalCarePlanData) {
                    // Restore original data for update mode
                    setSessionFormData(prev => ({
                      ...prev,
                      ...originalCarePlanData,
                    }));
                  } else if (carePlanEditMode === "create") {
                    // Restore original data for create mode (or clear if no original data)
                    if (originalCarePlanData) {
                      setSessionFormData(prev => ({
                        ...prev,
                        ...originalCarePlanData,
                      }));
                    } else {
                      // Clear care plan fields if no original data exists
                      setSessionFormData(prev => ({
                        ...prev,
                        clientGoal: "",
                        objectives: "",
                        interventions: "",
                        sessionFrequency: "",
                        otherSessionFrequencyText: "",
                        reviewDate: "",
                      }));
                    }
                  }
                  setCarePlanEditMode(null);
                }}
                onSave={handleCarePlanSave}
                saving={carePlanSaving}
              />
            </>
          )}
        </div>

        {/* Right Sidebar - Sign & Lock Card and Addendum Signatures */}
        {isSubmit && (!addAddendum || (editExistingAddendum && canEditAddendum())) && (
          <div style={{
            width: "29%",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            alignSelf: "flex-start"
          }}>
            <SignAndLockCard
              providerName={providerName}
              providerCredentials={providerCredentials}
              isSigned={isSigned}
              signingAndSubmitting={signingAndSubmitting}
              onSignAndLock={handleSignAndLock}
              signedAt={sessionFormData?.signedAt}
              canEditAddendum={canEditAddendum()}
            />

            {/* Show addendum signatures when signed */}
            {isSubmit && !addAddendum && addendumData && Array.isArray(addendumData) && addendumData.length > 0 && !canEditAddendum() && (
              <>
                {addendumData.map((addendum, index) => {
                  if (addendum.addendumSigned === true && addendum.addendumSignedAt) {
                    const addendumNumber = index + 1;
                    const addendumSignedDate = new Date(addendum.addendumSignedAt).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric"
                    });
                    const addendumSignedTime = new Date(addendum.addendumSignedAt).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true
                    });

                    return (
                      <Card key={`addendum-signature-${index}`} className={styles.card} style={{
                        width: "100%",
                        marginBottom: "0"
                      }}>
                        <h3 className={styles.sectionHeading} style={{ marginBottom: "24px", fontSize: "20px" }}>
                          Provider Signature - Addendum # {addendumNumber}
                        </h3>

                        <div className={styles.separator} style={{ width: "40%" }}></div>

                        <div style={{ marginBottom: "24px" }}>
                          <div style={{
                            padding: "20px",
                            border: "none !important",
                            borderRadius: "4px",
                            backgroundColor: "#ECF8FE",
                            minHeight: "150px",
                            textAlign: "left"
                          }}>
                            <div style={{
                              fontFamily: "'Brush Script MT', 'Brush Script', cursive",
                              fontSize: "32px",
                              color: "#1a1a1a",
                              marginBottom: "16px",
                              textAlign: "left",
                              fontWeight: "400",
                              letterSpacing: "1px"
                            }}>
                              {providerName || ""}
                            </div>
                            <div style={{
                              fontSize: "14px",
                              color: "#666",
                              marginBottom: "4px"
                            }}>
                              Signed by {providerName || ""}, {providerCredentials || ""}
                            </div>
                            <div style={{
                              fontSize: "14px",
                              color: "#666"
                            }}>
                              {addendumSignedDate}; {addendumSignedTime}
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  }
                  return null;
                })}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FollowUp;

